import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCountry } from "../contexts/CountryContext";
import { API_URL } from "../lib/supabase";
import { viewProviderDocument } from "../lib/documents";
import { pixelTrack } from "../lib/metaPixel";
import { formatDateNZ } from "../lib/format";
import {
  LayoutDashboard, User, Briefcase, FileText, CreditCard, MessageSquare,
  MapPin, Clock, Star, Shield, CheckCircle, Upload, X, ChevronRight,
  ArrowRight, ArrowLeft, Plus, Eye, Send, Calendar, DollarSign, TrendingUp,
  Users, Search, AlertCircle, XCircle, Trash2, Camera
} from "lucide-react";

// ============================================
// Provider Dashboard Layout with Sidebar
// ============================================
export const ProviderLayout = ({ children }) => {
  const { profile, isAuthenticated, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/login?redirect=' + encodeURIComponent(location.pathname));
      } else if (profile?.role !== 'provider') {
        navigate('/');
      }
    }
  }, [loading, isAuthenticated, profile, navigate, location.pathname]);

  if (loading) return <div className="loading" style={{ padding: '60px', textAlign: 'center' }}>Loading...</div>;
  if (!isAuthenticated || profile?.role !== 'provider') return null;

  const navItems = [
    { path: '/provider/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/provider/profile', label: 'Business Profile', icon: User },
    { path: '/provider/services', label: 'My Services', icon: Briefcase },
    { path: '/provider/quotes', label: 'Quote Leads', icon: MessageSquare },
    { path: '/provider/reviews', label: 'Reviews', icon: Star },
    { path: '/provider/documents', label: 'Verification', icon: FileText },
    { path: '/provider/subscription', label: 'Subscription', icon: CreditCard },
  ];

  return (
    <div className="provider-layout" data-testid="provider-layout">
      <aside className="provider-sidebar">
        <div className="provider-sidebar-header" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            className="provider-sidebar-avatar"
            style={{
              width: 44, height: 44, flexShrink: 0,
              borderRadius: '50%',
              backgroundImage: profile?.avatar_url ? `url(${profile.avatar_url})` : 'none',
              backgroundColor: profile?.avatar_url ? 'transparent' : 'rgba(15,23,42,0.08)',
              backgroundSize: 'cover', backgroundPosition: 'center',
              border: '1px solid rgba(15,23,42,0.10)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            data-testid="sidebar-avatar"
          >
            {!profile?.avatar_url && <User size={22} style={{ color: 'var(--text-muted)' }} />}
          </div>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{profile?.full_name || 'Provider'}</h3>
            <p style={{ margin: 0 }}>Provider Dashboard</p>
          </div>
        </div>
        <nav className="sidebar-nav">
          {navItems.map(item => (
            <Link
              key={item.path}
              to={item.path}
              className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
      </aside>
      <div className="provider-mobile-nav">
        {navItems.map(item => (
          <Link key={item.path} to={item.path} className={location.pathname === item.path ? 'active' : ''}>
            <item.icon size={16} />
            <span>{item.label}</span>
          </Link>
        ))}
      </div>
      <div className="provider-main">{children}</div>
    </div>
  );
};

// ============================================
// Provider 8-Step Onboarding Wizard
// ============================================
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const CITIES_NZ = ['Auckland', 'Wellington', 'Christchurch', 'Hamilton', 'Tauranga', 'Dunedin', 'Napier', 'Palmerston North', 'Nelson', 'Rotorua'];
const CITIES_AU = ['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Canberra', 'Hobart', 'Darwin', 'Cairns'];
const SUBURBS_NZ = {
  Auckland: ['CBD', 'Ponsonby', 'Grey Lynn', 'Mt Eden', 'Newmarket', 'Parnell', 'Remuera', 'Epsom', 'Mt Albert', 'Henderson', 'Manukau', 'Takapuna', 'Albany'],
  Wellington: ['CBD', 'Thorndon', 'Lambton Quay', 'Newtown', 'Karori', 'Miramar', 'Kilbirnie', 'Petone', 'Lower Hutt'],
  Christchurch: ['CBD', 'Riccarton', 'Fendalton', 'Merivale', 'Papanui', 'Hornby', 'Linwood'],
  Hamilton: ['CBD', 'Hillcrest', 'Rototuna', 'Te Rapa', 'Chartwell'],
  Tauranga: ['CBD', 'Mt Maunganui', 'Papamoa', 'Greerton', 'Bethlehem'],
  Dunedin: ['CBD', 'South Dunedin', 'St Kilda', 'Mosgiel'],
};
const SUBURBS_AU = {
  Sydney: ['CBD', 'Bondi', 'Surry Hills', 'Newtown', 'Parramatta', 'Manly', 'Chatswood', 'Bankstown', 'Liverpool', 'Penrith'],
  Melbourne: ['CBD', 'Southbank', 'St Kilda', 'Fitzroy', 'Carlton', 'Richmond', 'South Yarra', 'Footscray', 'Brunswick'],
  Brisbane: ['CBD', 'Fortitude Valley', 'South Bank', 'West End', 'Paddington', 'Bulimba', 'Carindale'],
  Perth: ['CBD', 'Fremantle', 'Subiaco', 'Northbridge', 'Joondalup', 'Rockingham'],
  Adelaide: ['CBD', 'Glenelg', 'Norwood', 'Unley', 'Prospect', 'Marion'],
  'Gold Coast': ['Surfers Paradise', 'Broadbeach', 'Burleigh Heads', 'Southport', 'Robina'],
};

export const ProviderOnboardingPage = () => {
  const { session, profile } = useAuth();
  const { countryCode, country, showToggle } = useCountry();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [allServices, setAllServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadedDocs, setUploadedDocs] = useState([]);

  const [form, setForm] = useState({
    business_name: '', business_email: profile?.email || '', business_phone: '',
    bio: '', years_experience: '', hourly_rate_min: '', hourly_rate_max: '',
    country_code: countryCode, city: '',
    suburbs_served: [], service_radius_km: 25,
    availability: DAYS.reduce((a, d) => ({ ...a, [d]: { enabled: d !== 'Saturday' && d !== 'Sunday', start: '08:00', end: '17:00' } }), {}),
    weekend_available: false, same_day_available: false,
    selected_services: [],
    missing_service_name: '', missing_service_category: '', missing_service_desc: '',
    missing_service_mode: 'existing',   // 'existing' or 'new'
    missing_new_category_name: '',
    subscription_plan: 'monthly'
  });

  const [serviceRequests, setServiceRequests] = useState([]);
  const [error, setError] = useState('');
  const [svcRequestError, setSvcRequestError] = useState('');     // inline, beside the form
  const [svcRequestSuccess, setSvcRequestSuccess] = useState(''); // inline success message
  const [providerCreated, setProviderCreated] = useState(false);

  // Onboarding Step 3 — category dropdown filter (preserves selected services
  // across category switches; "" = show all)
  const [step3CatFilter, setStep3CatFilter] = useState('');

  // ── Onboarding-draft persistence (Feb 2026 launch fix) ─────────────────
  // Restore onboarding state from sessionStorage on mount so the provider
  // never loses progress (e.g. browser back from Stripe Checkout, refresh,
  // accidental tab close). All form fields + current step are preserved.
  // Cleared on successful completion (handleFinalSubmit's redirect).
  const STORAGE_KEY = 'handyhub.onboarding.draft';
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved.form) setForm(prev => ({ ...prev, ...saved.form }));
        if (saved.step && saved.step >= 1 && saved.step <= 7) setStep(saved.step);
        if (saved.providerCreated) setProviderCreated(true);
      }
    } catch (e) {
      // Corrupted snapshot — ignore and start fresh
      // eslint-disable-next-line no-console
      console.warn('[Onboarding] could not restore draft from sessionStorage:', e);
    }
    setHydrated(true);
  }, []);

  // Persist form + step to sessionStorage whenever they change (after hydrate)
  useEffect(() => {
    if (!hydrated) return;
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ form, step, providerCreated }));
    } catch {
      // Quota exceeded or storage disabled — silent no-op
    }
  }, [form, step, providerCreated, hydrated]);

  useEffect(() => { fetchData(); }, []);

  // Pixel: provider onboarding STARTED — fire once per session when this page mounts
  useEffect(() => {
    if (!window.__pixelOnboardingStartedFired) {
      window.__pixelOnboardingStartedFired = true;
      pixelTrack('InitiateCheckout', {
        content_name: 'Provider Onboarding Started',
        content_category: 'provider_onboarding',
      });
    }
  }, []);

  // Scroll to top whenever the onboarding step changes (Issue 2 — wizard UX fix)
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [step]);

  // When the user lands on / returns to the Verification step, pull existing
  // uploaded documents from the server so they re-appear (in case the user navigated back).
  useEffect(() => {
    if (step !== 4 || !providerCreated || !session?.access_token) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/provider/documents`, {
          headers: { 'Authorization': `Bearer ${session.access_token}` },
        });
        if (r.ok) {
          const docs = await r.json();
          setUploadedDocs(docs);
        }
      } catch (err) {
        console.warn('[Onboarding] Could not refetch documents on step 4:', err);
      }
    })();
  }, [step, providerCreated, session?.access_token]);

  // Build the same payload the final /provider/onboarding endpoint expects.
  // Used for the mid-onboarding /draft call so the verification step has a real provider row.
  const buildOnboardingPayload = () => ({
    business_name: form.business_name || 'Pending Business',
    business_email: form.business_email || profile?.email || '',
    business_phone: form.business_phone || '',
    bio: form.bio || '',
    years_experience: parseInt(form.years_experience, 10) || 0,
    hourly_rate_min: form.hourly_rate_min ? parseFloat(form.hourly_rate_min) : null,
    hourly_rate_max: form.hourly_rate_max ? parseFloat(form.hourly_rate_max) : null,
    country_code: form.country_code,
    city: form.city || '',
    suburbs_served: form.suburbs_served || [],
    service_radius_km: form.service_radius_km || 25,
    availability: form.availability,
    weekend_available: !!form.weekend_available,
    same_day_available: !!form.same_day_available,
    selected_services: form.selected_services || [],
  });

  // Create or update a draft provider row when transitioning into the Verification step.
  // Idempotent — safe to call repeatedly. Resolves Issue 3 (upload during onboarding).
  const ensureDraftProvider = async () => {
    let res;
    try {
      res = await fetch(`${API_URL}/provider/onboarding/draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(buildOnboardingPayload()),
      });
    } catch (err) {
      console.error('[Onboarding] draft save network error:', err);
      setError(`Network error while saving your details: ${err?.message || 'unknown'}. Please try again.`);
      return false;
    }

    // Read body ONCE, tolerant of Emergent fetch-interceptor pre-consumption
    let text = ''; try { text = await res.text(); } catch { text = ''; }
    let data = null; try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }

    if (res.ok && data?.ok) {
      setProviderCreated(true);
      return true;
    }

    // Surface the real problem — with a specific message per HTTP status
    console.error('[Onboarding] draft save failed:', res.status, data);
    let msg = '';
    if (res.status === 401) {
      msg = 'Your session has expired. Please refresh the page and log in again.';
    } else if (res.status === 403) {
      msg = data?.detail || 'You do not have permission to perform this action.';
    } else if (res.status === 422) {
      msg = `Some details look invalid — ${data?.detail || 'please check your input and try again.'}`;
    } else if (res.status >= 500) {
      msg = `Server error (HTTP ${res.status}). ${data?.detail || 'Please try again in a moment.'}`;
    } else if (data?.detail) {
      msg = `Could not save your details: ${data.detail}`;
    } else {
      msg = `Could not save your details (HTTP ${res.status}). Please try again.`;
    }
    setError(msg);
    return false;
  };

  const goToNextStep = async () => {
    setError('');
    // Before entering step 4 (Verification), persist a draft provider row so uploads work.
    if (step === 3) {
      const ok = await ensureDraftProvider();
      if (!ok) return;
    }
    setStep(step + 1);
  };

  const fetchData = async () => {
    try {
      const [catRes, svcRes] = await Promise.all([fetch(`${API_URL}/categories`), fetch(`${API_URL}/services`)]);
      setCategories(await catRes.json());
      setAllServices(await svcRes.json());
    } catch (err) { console.error(err); }
  };

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleSuburb = (suburb) => {
    setForm(prev => ({
      ...prev,
      suburbs_served: prev.suburbs_served.includes(suburb)
        ? prev.suburbs_served.filter(s => s !== suburb)
        : [...prev.suburbs_served, suburb]
    }));
  };

  const toggleService = (id) => {
    setForm(prev => ({
      ...prev,
      selected_services: prev.selected_services.includes(id)
        ? prev.selected_services.filter(x => x !== id)
        : [...prev.selected_services, id]
    }));
  };

  const toggleDay = (day) => {
    setForm(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { ...prev.availability[day], enabled: !prev.availability[day].enabled }
      }
    }));
  };

  const updateTime = (day, field, value) => {
    setForm(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: { ...prev.availability[day], [field]: value }
      }
    }));
  };

  const handleDocUpload = async (docType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Client-side validation — fail fast with a specific message instead of a generic one
      const MAX_SIZE_MB = 10;
      const maxBytes = MAX_SIZE_MB * 1024 * 1024;
      const allowedExts = ['pdf', 'jpg', 'jpeg', 'png'];
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!allowedExts.includes(ext)) {
        setError(`"${file.name}" — unsupported file type. Please upload a PDF, JPG, or PNG.`);
        return;
      }
      if (file.size > maxBytes) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        setError(`"${file.name}" is ${sizeMb} MB — files must be smaller than ${MAX_SIZE_MB} MB.`);
        return;
      }
      if (file.size === 0) {
        setError(`"${file.name}" appears to be empty (0 bytes). Please choose a valid file.`);
        return;
      }

      setUploadingDoc(true);
      setError('');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('doc_type', docType);

      console.log(`[DocUpload] Uploading ${docType}: ${file.name} (${file.type}, ${(file.size/1024).toFixed(1)} KB)`);

      // Step 1 — make the request. ONLY a true network failure goes to catch.
      let res;
      try {
        res = await fetch(`${API_URL}/provider/documents`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session.access_token}` },
          body: formData
        });
      } catch (networkErr) {
        console.error('[DocUpload] Network error:', networkErr);
        setError(`Network error during upload: ${networkErr?.message || 'unknown'}. Please check your connection.`);
        setUploadingDoc(false);
        return;
      }

      // Step 2 — read the body ONCE, defensively (Emergent fetch interceptor may have
      // already consumed the stream on non-2xx responses; tolerate that without throwing).
      let text = '';
      try {
        text = await res.text();
      } catch (readErr) {
        console.warn('[DocUpload] Body already consumed by interceptor:', readErr?.message);
        text = '';
      }
      let body = null;
      try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }

      try {
        if (res.ok && body?.id) {
          console.log('[DocUpload] Upload success response:', body);
          // Tag with file_name from the input (backend stores it but be defensive),
          // ensure verification_type is set (this is how the UI filter matches).
          const newDoc = {
            ...body,
            file_name: body.file_name || file.name,
          };
          setUploadedDocs(prev => {
            // De-dupe by id in case of double-click + late refetch
            const filtered = prev.filter(d => d.id !== newDoc.id);
            return [...filtered, newDoc];
          });
          setError('');
          // Belt-and-braces: refetch the canonical list so the UI reflects server state
          try {
            const refresh = await fetch(`${API_URL}/provider/documents`, {
              headers: { 'Authorization': `Bearer ${session.access_token}` },
            });
            if (refresh.ok) {
              const docs = await refresh.json();
              setUploadedDocs(docs);
            }
          } catch (e) { /* non-fatal */ }
        } else {
          // Surface the real backend error instead of a generic message
          const backendMsg = body?.detail || body?.message || body?.raw || '';
          console.error('[DocUpload] Upload FAILED:', res.status, body);
          if (res.status === 401) {
            setError('Your session has expired. Please refresh the page and log in again.');
          } else if (res.status === 404) {
            setError('Provider profile not found. Please complete the earlier onboarding steps first.');
          } else if (res.status === 413) {
            setError(`File too large. Maximum upload size is 10 MB.`);
          } else if (res.status === 429) {
            setError('Too many uploads in a short time — please wait a moment and try again.');
          } else if (backendMsg) {
            setError(`Upload failed: ${backendMsg}`);
          } else {
            setError(`Upload failed (HTTP ${res.status}). Please try again.`);
          }
        }
      } finally {
        setUploadingDoc(false);
      }
    };
    input.click();
  };

  const handleMissingServiceSubmit = async () => {
    setSvcRequestError('');
    setSvcRequestSuccess('');

    const mode = form.missing_service_mode || 'existing';
    const svcName = (form.missing_service_name || '').trim();
    if (!svcName) {
      setSvcRequestError('Please enter the service name.');
      return;
    }

    const payload = {
      service_name: svcName,
      description: form.missing_service_desc || '',
    };
    if (mode === 'new') {
      const newCat = (form.missing_new_category_name || '').trim();
      if (!newCat) {
        setSvcRequestError('Please enter the new category name.');
        return;
      }
      payload.new_category_name = newCat;
    } else {
      if (!form.missing_service_category) {
        setSvcRequestError('Please select an existing category.');
        return;
      }
      payload.category_id = form.missing_service_category;
    }

    // Single fetch, single body read (tolerates Emergent fetch interceptor)
    let res;
    try {
      res = await fetch(`${API_URL}/provider/service-requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify(payload),
      });
    } catch (netErr) {
      setSvcRequestError(`Network error: ${netErr?.message || 'unknown'}. Please try again.`);
      return;
    }
    let text = '';
    try { text = await res.text(); } catch { text = ''; }
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = { raw: text }; }

    if (res.ok && data?.service_request) {
      setServiceRequests(prev => [...prev, data.service_request]);
      update('missing_service_name', '');
      update('missing_service_desc', '');
      update('missing_service_category', '');
      update('missing_new_category_name', '');
      setSvcRequestSuccess(data.message || 'Service request submitted for review.');
    } else {
      const detail = data?.detail || data?.message || '';
      setSvcRequestError(detail || `Failed to submit service request (HTTP ${res.status}).`);
    }
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      // Step A: Create provider profile if not yet created
      if (!providerCreated) {
        const weekendEnabled = form.availability.Saturday?.enabled || form.availability.Sunday?.enabled;
        const payload = {
          business_name: form.business_name,
          business_email: form.business_email,
          business_phone: form.business_phone,
          bio: form.bio,
          years_experience: form.years_experience ? parseInt(form.years_experience) : null,
          hourly_rate_min: form.hourly_rate_min ? parseFloat(form.hourly_rate_min) : null,
          hourly_rate_max: form.hourly_rate_max ? parseFloat(form.hourly_rate_max) : null,
          country_code: form.country_code,
          city: form.city,
          suburbs_served: form.suburbs_served,
          service_radius_km: form.service_radius_km,
          availability: form.availability,
          weekend_available: weekendEnabled,
          same_day_available: form.same_day_available,
          selected_services: form.selected_services,
        };

        const profileRes = await fetch(`${API_URL}/provider/onboarding`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
          body: JSON.stringify(payload)
        });

        if (!profileRes.ok) {
          let errData = null;
          try { const t = await profileRes.text(); errData = JSON.parse(t); } catch(e) { errData = null; }
          setError(errData?.detail || 'Failed to create provider profile. Please try again.');
          setLoading(false);
          return;
        }
        setProviderCreated(true);
        // Pixel: provider onboarding profile COMPLETED (before payment step).
        // We track this separately from Stripe Purchase so we capture funnel
        // drop-off between profile-complete and Stripe-checkout-complete.
        pixelTrack('CompleteRegistration', {
          content_name: 'Provider Onboarding Completed',
          content_category: 'provider_onboarding',
          status: 'awaiting_subscription',
        });
      }

      // Step B: Create Stripe checkout session
      const checkoutRes = await fetch(`${API_URL}/provider/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          plan: form.subscription_plan,
          origin_url: window.location.origin
        })
      });

      let checkoutData = null;
      try {
        const text = await checkoutRes.text();
        checkoutData = JSON.parse(text);
      } catch (e) {
        checkoutData = null;
      }

      if (!checkoutRes.ok || !checkoutData?.checkout_url) {
        setError(checkoutData?.detail || 'Failed to start checkout. Please try again.');
        setLoading(false);
        return;
      }

      // Onboarding is now complete from our app's perspective (provider row
      // exists + checkout session created). Wipe the persisted draft so a
      // future visit doesn't restore stale data. The user is about to leave
      // this tab for Stripe Checkout — sessionStorage is scoped per-tab so
      // this clears any zombie state.
      try { sessionStorage.removeItem('handyhub.onboarding.draft'); } catch { /* noop */ }

      window.location.href = checkoutData.checkout_url;
    } catch (err) {
      console.error('Submit error:', err);
      setError('A network error occurred. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const STEPS = [
    { num: 1, label: 'Business' },
    { num: 2, label: 'Location' },
    { num: 3, label: 'Services' },
    { num: 4, label: 'Verification' },
    { num: 5, label: 'Review' },
    { num: 6, label: 'Subscription' },
    { num: 7, label: 'Submit' },
  ];

  const [showMissingSvcForm, setShowMissingSvcForm] = useState(false);

  const canProceed = () => {
    if (step === 1) return form.business_name.trim().length > 0;
    if (step === 2) return form.city.length > 0;
    if (step === 3) return form.selected_services.length > 0;
    return true;
  };

  // Form's country (provider-selected) drives the city/suburb list,
  // falling back to the legacy local constants if the lib lookup is empty.
  const cityList = (form.country_code === 'NZ' ? CITIES_NZ : CITIES_AU);
  const suburbMap = form.country_code === 'NZ' ? SUBURBS_NZ : SUBURBS_AU;
  const suburbList = suburbMap[form.city] || [];

  const servicesByCategory = categories.map(cat => ({
    ...cat,
    services: allServices.filter(s => s.category_id === cat.id)
  })).filter(cat => cat.services.length > 0);

  return (
    <div className="onboarding-page" data-testid="provider-onboarding">
      <div className="onboarding-header">
        <h1>Become a HandyHub Provider</h1>
        <p>Complete these steps to start receiving quote leads</p>
      </div>

      {/* Step indicators */}
      <div className="onboarding-steps">
        {STEPS.map((s, i) => (
          <React.Fragment key={s.num}>
            {i > 0 && <div className={`step-connector ${step > s.num - 1 ? 'completed' : ''}`} />}
            <div className="onboarding-step">
              <div
                className={`step-dot ${step === s.num ? 'active' : step > s.num ? 'completed' : ''}`}
                onClick={() => s.num < step && setStep(s.num)}
                style={{ cursor: s.num < step ? 'pointer' : 'default' }}
              >
                {step > s.num ? <CheckCircle size={16} /> : s.num}
              </div>
              <span className={`step-label ${step === s.num ? 'active' : ''}`}>{s.label}</span>
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className="onboarding-card">
        {/* Error display */}
        {error && (
          <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '18px', fontSize: '0.875rem', color: '#DC2626', display: 'flex', gap: '8px', alignItems: 'flex-start' }} data-testid="onboarding-error">
            <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
            <span>{error}</span>
            <button onClick={() => setError('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', marginLeft: 'auto', padding: 0 }}><X size={14} /></button>
          </div>
        )}
        {/* STEP 1: Business Details */}
        {step === 1 && (
          <div data-testid="step-1-business">
            <h2>Business Information</h2>
            <div className="profile-form-grid">
              <div className="form-group">
                <label>Business Name *</label>
                <input type="text" value={form.business_name} onChange={(e) => update('business_name', e.target.value)} placeholder="Your business name" data-testid="business-name-input" />
              </div>
              <div className="form-group">
                <label>Business Email</label>
                <input type="email" value={form.business_email} onChange={(e) => update('business_email', e.target.value)} placeholder="contact@business.com" data-testid="business-email-input" />
              </div>
              <div className="form-group">
                <label>Business Phone</label>
                <input type="tel" value={form.business_phone} onChange={(e) => update('business_phone', e.target.value)} placeholder={country.phonePlaceholder} data-testid="business-phone-input" />
              </div>
              <div className="form-group">
                <label>Years of Experience</label>
                <input type="number" value={form.years_experience} onChange={(e) => update('years_experience', e.target.value)} placeholder="e.g. 5" data-testid="years-exp-input" />
              </div>
              <div className="form-group full-width">
                <label>Hourly Rate Range ($)</label>
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <input type="number" value={form.hourly_rate_min} onChange={(e) => update('hourly_rate_min', e.target.value)} placeholder="Min" style={{ flex: 1 }} data-testid="rate-min-input" />
                  <span style={{ color: 'var(--text-muted)' }}>to</span>
                  <input type="number" value={form.hourly_rate_max} onChange={(e) => update('hourly_rate_max', e.target.value)} placeholder="Max" style={{ flex: 1 }} data-testid="rate-max-input" />
                </div>
              </div>
              <div className="form-group full-width">
                <label>About Your Business</label>
                <textarea value={form.bio} onChange={(e) => update('bio', e.target.value)} placeholder="Tell customers about your experience, specialties, and what makes you unique..." rows={4} data-testid="bio-textarea" />
              </div>
            </div>
          </div>
        )}

        {/* STEP 2: Location / Service Area / Availability */}
        {step === 2 && (
          <div data-testid="step-2-location">
            <h2>Location & Availability</h2>

            <div className="profile-form-grid" style={{ marginBottom: '24px' }}>
              {showToggle ? (
                <div className="form-group">
                  <label>Country</label>
                  <select value={form.country_code} onChange={(e) => { update('country_code', e.target.value); update('city', ''); update('suburbs_served', []); }} data-testid="country-select">
                    <option value="NZ">New Zealand</option>
                    <option value="AU">Australia</option>
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label>Country</label>
                  <input type="text" value={country.name} readOnly disabled data-testid="country-locked" style={{ background: 'var(--surface-hover)', cursor: 'not-allowed' }} />
                </div>
              )}
              <div className="form-group">
                <label>City *</label>
                <select value={form.city} onChange={(e) => { update('city', e.target.value); update('suburbs_served', []); }} data-testid="city-select">
                  <option value="">Select city</option>
                  {cityList.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group full-width">
                <label>Service Radius (km)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <input type="range" min="5" max="100" step="5" value={form.service_radius_km} onChange={(e) => update('service_radius_km', parseInt(e.target.value))} style={{ flex: 1 }} data-testid="radius-slider" />
                  <span style={{ minWidth: '50px', fontWeight: 600, fontSize: '0.9375rem' }}>{form.service_radius_km} km</span>
                </div>
              </div>
            </div>

            {suburbList.length > 0 && (
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: '10px', fontSize: '0.9375rem' }}>Suburbs You Serve</label>
                <div className="service-select-grid">
                  {suburbList.map(sub => (
                    <label key={sub} className={`service-select-item ${form.suburbs_served.includes(sub) ? 'selected' : ''}`} data-testid={`suburb-${sub.toLowerCase().replace(/\s/g, '-')}`}>
                      <input type="checkbox" checked={form.suburbs_served.includes(sub)} onChange={() => toggleSuburb(sub)} />
                      <span>{sub}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div style={{ marginBottom: '24px' }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: '10px', fontSize: '0.9375rem' }}>Weekly Availability</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {DAYS.map(day => (
                  <div key={day} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 14px', background: form.availability[day].enabled ? 'rgba(245,158,11,0.04)' : 'var(--surface-hover)', border: `1px solid ${form.availability[day].enabled ? 'var(--accent)' : 'var(--border-color)'}`, borderRadius: 'var(--radius-md)' }} data-testid={`day-${day.toLowerCase()}`}>
                    <input type="checkbox" checked={form.availability[day].enabled} onChange={() => toggleDay(day)} style={{ accentColor: 'var(--accent)' }} />
                    <span style={{ minWidth: '90px', fontWeight: 500, fontSize: '0.875rem' }}>{day}</span>
                    {form.availability[day].enabled && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                        <input type="time" value={form.availability[day].start} onChange={(e) => updateTime(day, 'start', e.target.value)} style={{ padding: '4px 8px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem' }} />
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>to</span>
                        <input type="time" value={form.availability[day].end} onChange={(e) => updateTime(day, 'end', e.target.value)} style={{ padding: '4px 8px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-sm)', fontSize: '0.8125rem' }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <label className={`service-select-item ${form.same_day_available ? 'selected' : ''}`} style={{ flex: '1 1 200px' }} data-testid="same-day-toggle">
                <input type="checkbox" checked={form.same_day_available} onChange={(e) => update('same_day_available', e.target.checked)} />
                <span>Same-day availability</span>
              </label>
            </div>
          </div>
        )}

        {/* STEP 3: Select Services (with inline "Request missing service" panel) */}
        {step === 3 && (
          <div data-testid="step-3-services">
            <h2>Select Your Services</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '20px' }}>
              Choose the services you offer ({form.selected_services.length} selected)
            </p>

            {/* Category dropdown filter — fixes "too much scrolling" on step 3 */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
              <label htmlFor="onboarding-cat-filter" style={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                Filter by category:
              </label>
              <select
                id="onboarding-cat-filter"
                data-testid="onboarding-category-filter"
                value={step3CatFilter}
                onChange={e => setStep3CatFilter(e.target.value)}
                style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', fontSize: '0.875rem', minWidth: '220px', background: '#fff' }}
              >
                <option value="">All categories ({servicesByCategory.length})</option>
                {servicesByCategory.map(c => {
                  const sel = c.services.filter(s => form.selected_services.includes(s.id)).length;
                  return (
                    <option key={c.id} value={c.id}>
                      {c.name}{sel > 0 ? ` — ${sel} selected` : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            {(step3CatFilter ? servicesByCategory.filter(c => c.id === step3CatFilter) : servicesByCategory).map(cat => {
              const sel = cat.services.filter(s => form.selected_services.includes(s.id)).length;
              return (
                <div key={cat.id} style={{ marginBottom: '20px' }}>
                  <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '10px', color: 'var(--text-dark)', display: 'flex', alignItems: 'center', gap: 8 }}>
                    {cat.name}
                    {sel > 0 && (
                      <span style={{ padding: '2px 8px', background: 'rgba(255,107,53,0.12)', color: 'var(--accent)', borderRadius: '50px', fontSize: '0.6875rem', fontWeight: 700 }}>
                        {sel} selected
                      </span>
                    )}
                  </h3>
                  <div className="service-select-grid">
                    {cat.services.map(svc => (
                      <label key={svc.id} className={`service-select-item ${form.selected_services.includes(svc.id) ? 'selected' : ''}`} data-testid={`svc-opt-${svc.slug}`}>
                        <input type="checkbox" checked={form.selected_services.includes(svc.id)} onChange={() => toggleService(svc.id)} />
                        <span>{svc.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}

            {/* Inline "Can't find your service?" request panel */}
            <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '1px dashed var(--border-color)' }}>
              <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)', marginBottom: '10px' }}>
                Can't find your service? You can request it now or skip this step and add it later.
              </p>
              {!showMissingSvcForm ? (
                <button
                  type="button"
                  onClick={() => setShowMissingSvcForm(true)}
                  data-testid="toggle-missing-svc-form"
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                    color: 'var(--accent)', fontSize: '0.875rem', fontWeight: 600,
                    display: 'inline-flex', alignItems: 'center', gap: '6px',
                  }}
                >
                  <Plus size={14} /> Request a service
                </button>
              ) : (
                <div style={{ background: 'rgba(245,158,11,0.04)', border: '1px solid rgba(245,158,11,0.20)', borderRadius: 'var(--radius-md)', padding: '18px 20px' }} data-testid="inline-missing-svc-form">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <h4 style={{ fontSize: '0.9375rem', fontWeight: 600, margin: 0 }}>Request a Service</h4>
                    <button
                      type="button"
                      onClick={() => { setShowMissingSvcForm(false); setSvcRequestError(''); setSvcRequestSuccess(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--text-muted)' }}
                      data-testid="close-missing-svc-form"
                      aria-label="Close"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', lineHeight: 1.5, margin: '0 0 14px' }}>
                    Requested services are reviewed before being added to the platform. This is optional — you can continue onboarding without submitting a request.
                  </p>

                  {/* Inline error and success messages (beside the form, not at step top) */}
                  {svcRequestError && (
                    <div className="inline-alert inline-alert-error" role="alert" data-testid="svc-request-error">
                      <AlertCircle size={14} /> {svcRequestError}
                    </div>
                  )}
                  {svcRequestSuccess && (
                    <div className="inline-alert inline-alert-success" role="status" data-testid="svc-request-success">
                      <CheckCircle size={14} /> {svcRequestSuccess}
                    </div>
                  )}

                  {/* Mode toggle */}
                  <div className="form-group" style={{ marginBottom: '14px' }}>
                    <label style={{ display: 'block', marginBottom: '8px' }}>Request type</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', alignItems: 'center', fontSize: '0.8125rem' }}>
                        <input
                          type="radio"
                          name="missing-svc-mode"
                          value="existing"
                          checked={(form.missing_service_mode || 'existing') === 'existing'}
                          onChange={() => update('missing_service_mode', 'existing')}
                          data-testid="missing-svc-mode-existing"
                        />
                        <span>Request a service under an existing category</span>
                      </label>
                      <label style={{ display: 'flex', gap: '8px', cursor: 'pointer', alignItems: 'center', fontSize: '0.8125rem' }}>
                        <input
                          type="radio"
                          name="missing-svc-mode"
                          value="new"
                          checked={form.missing_service_mode === 'new'}
                          onChange={() => update('missing_service_mode', 'new')}
                          data-testid="missing-svc-mode-new"
                        />
                        <span>Request a new category and service</span>
                      </label>
                    </div>
                  </div>

                  {(form.missing_service_mode || 'existing') === 'existing' ? (
                    <div className="form-group">
                      <label>Service Category</label>
                      <select value={form.missing_service_category} onChange={(e) => update('missing_service_category', e.target.value)} data-testid="missing-svc-category">
                        <option value="">Select a category</option>
                        {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label>New Category Name</label>
                      <input
                        type="text"
                        value={form.missing_new_category_name}
                        onChange={(e) => update('missing_new_category_name', e.target.value)}
                        placeholder="e.g. Drone Photography"
                        data-testid="missing-new-category-name"
                      />
                    </div>
                  )}

                  <div className="form-group">
                    <label>Service Name</label>
                    <input type="text" value={form.missing_service_name} onChange={(e) => update('missing_service_name', e.target.value)} placeholder="e.g. Solar Panel Installation" data-testid="missing-svc-name" />
                  </div>
                  <div className="form-group">
                    <label>Description (optional)</label>
                    <textarea value={form.missing_service_desc} onChange={(e) => update('missing_service_desc', e.target.value)} placeholder="Briefly describe the service..." rows={3} data-testid="missing-svc-desc" />
                  </div>

                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <button
                      className="btn-primary"
                      onClick={handleMissingServiceSubmit}
                      style={{ fontSize: '0.8125rem' }}
                      data-testid="submit-missing-svc"
                    >
                      <Plus size={14} /> Submit Service Request
                    </button>
                    <button
                      type="button"
                      className="btn-outline"
                      onClick={() => { setShowMissingSvcForm(false); setSvcRequestError(''); setSvcRequestSuccess(''); }}
                      style={{ fontSize: '0.8125rem' }}
                      data-testid="skip-missing-svc"
                    >
                      Skip for now
                    </button>
                  </div>

                  {serviceRequests.length > 0 && (
                    <div style={{ marginTop: '18px' }}>
                      <h5 style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '8px' }}>Your Submitted Requests</h5>
                      {serviceRequests.map((sr, i) => (
                        <div key={i} style={{ padding: '10px 14px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', marginBottom: '6px', fontSize: '0.8125rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span>{sr.name}</span>
                          <span className="status-badge status-submitted">Pending Review</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 4: Verification Documents */}
        {step === 4 && (
          <div data-testid="step-4-verification">
            <h2>Verification Documents</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '20px' }}>
              Upload a government-issued photo ID to earn the <strong>ID Verified</strong> badge. Verified providers earn more trust and receive priority leads.
            </p>

            {[
              { key: 'government_id', label: 'Government ID', desc: "Driver's licence, passport, or national ID", backendType: 'id' },
            ].map(dt => {
              // Backend stores `verification_type` (id) — onboarding only collects ID verification.
              // Business and insurance document uploads were removed (Feb 2026).
              const uploaded = uploadedDocs.filter(
                d => d.verification_type === dt.backendType || d.document_type === dt.key
              );
              return (
                <div key={dt.key} style={{ marginBottom: '16px', padding: '18px', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-card)', background: 'var(--card-bg)' }} data-testid={`doc-${dt.key}`}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <div>
                      <h4 style={{ fontSize: '0.9375rem', fontWeight: 600 }}>{dt.label}</h4>
                      <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{dt.desc}</p>
                    </div>
                    {uploaded.length > 0 && <span className="status-badge status-submitted">Uploaded</span>}
                  </div>
                  {uploaded.map((doc, i) => (
                    <div key={doc.id || i} className="uploaded-file" style={{ marginBottom: '6px' }}>
                      <span><FileText size={14} /> {doc.file_name || 'Document'}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <button
                          type="button"
                          className="doc-view-btn"
                          onClick={async () => {
                            const r = await viewProviderDocument({
                              verificationId: doc.id,
                              accessToken: session?.access_token,
                              apiUrl: API_URL,
                            });
                            if (!r.ok) setError(r.error);
                          }}
                          data-testid={`view-doc-${doc.id}`}
                        >
                          <Eye size={13} /> View
                        </button>
                        {doc.status !== 'approved' && (
                          <button
                            type="button"
                            className="doc-view-btn doc-remove-btn"
                            onClick={async () => {
                              if (!window.confirm('Remove this document?')) return;
                              const res = await fetch(`${API_URL}/provider/documents/${doc.id}`, {
                                method: 'DELETE',
                                headers: { 'Authorization': `Bearer ${session.access_token}` },
                              });
                              if (res.ok) {
                                setUploadedDocs(prev => prev.filter(d => d.id !== doc.id));
                              } else {
                                let body = null; try { body = await res.json(); } catch {}
                                setError(body?.detail || `Could not remove document (HTTP ${res.status}).`);
                              }
                            }}
                            data-testid={`remove-doc-${doc.id}`}
                          >
                            <Trash2 size={13} /> Remove
                          </button>
                        )}
                        <span className={`status-badge ${doc.status === 'approved' ? 'status-completed' : doc.status === 'rejected' ? 'status-cancelled' : 'status-submitted'}`} style={{ fontSize: '0.6875rem' }}>
                          {doc.status === 'approved' ? 'Approved' : doc.status === 'rejected' ? 'Rejected' : 'Pending review'}
                        </span>
                      </div>
                    </div>
                  ))}
                  <button className="btn-outline" onClick={() => handleDocUpload(dt.key)} disabled={uploadingDoc} style={{ fontSize: '0.8125rem', marginTop: '8px' }} data-testid={`upload-${dt.key}`}>
                    {uploadingDoc ? (
                      <><span className="upload-spinner" /> Uploading…</>
                    ) : (
                      <><Upload size={14} /> {uploaded.length > 0 ? 'Upload Another' : 'Upload'}</>
                    )}
                  </button>
                </div>
              );
            })}

            <p style={{ marginTop: '6px', fontSize: '0.8125rem', color: 'var(--text-muted)', lineHeight: 1.6, fontStyle: 'italic' }} data-testid="onboarding-step-disclaimer">
              HandyHub reviews provider information during onboarding, however customers should independently assess providers before engaging services. You remain responsible for holding and maintaining any licences, registrations, or insurance required by law for your trade.
            </p>
          </div>
        )}

        {/* STEP 5: Review Summary */}
        {step === 5 && (
          <div data-testid="step-5-review">
            <h2>Review Your Profile</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <ReviewSection title="Business Details">
                <p style={{ fontWeight: 600, fontSize: '1rem' }}>{form.business_name}</p>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>{form.bio || 'No bio provided'}</p>
                {(form.hourly_rate_min || form.hourly_rate_max) && (
                  <p style={{ color: 'var(--accent)', fontWeight: 600, marginTop: '6px' }}>${form.hourly_rate_min || '?'} - ${form.hourly_rate_max || '?'}/hr</p>
                )}
              </ReviewSection>

              <ReviewSection title="Location & Availability">
                <p style={{ fontSize: '0.875rem' }}><MapPin size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />{form.city}, {form.country_code === 'NZ' ? 'New Zealand' : 'Australia'} ({form.service_radius_km} km radius)</p>
                {form.suburbs_served.length > 0 && (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '6px' }}>
                    {form.suburbs_served.map(s => <span key={s} className="service-tag" style={{ padding: '4px 8px', fontSize: '0.75rem' }}>{s}</span>)}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '10px', marginTop: '8px', fontSize: '0.8125rem' }}>
                  {form.same_day_available && <span style={{ color: 'var(--success)', fontWeight: 500 }}><CheckCircle size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> Same-day</span>}
                  {(form.availability.Saturday?.enabled || form.availability.Sunday?.enabled) && (
                    <span style={{ color: 'var(--success)', fontWeight: 500 }}><CheckCircle size={12} style={{ display: 'inline', verticalAlign: 'middle' }} /> Weekends</span>
                  )}
                </div>
              </ReviewSection>

              <ReviewSection title={`Services (${form.selected_services.length})`}>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  {form.selected_services.map(id => {
                    const svc = allServices.find(s => s.id === id);
                    return svc ? <span key={id} className="service-tag" style={{ padding: '5px 10px' }}>{svc.name}</span> : null;
                  })}
                  {form.selected_services.length === 0 && <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No services selected</p>}
                </div>
              </ReviewSection>

              <ReviewSection title="Verification">
                {uploadedDocs.length > 0 ? uploadedDocs.map((d, i) => (
                  <div key={i} style={{ fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 0' }}>
                    <FileText size={14} /> {d.document_type}: {d.file_name}
                  </div>
                )) : <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>No documents uploaded yet</p>}
              </ReviewSection>

              <ReviewSection title="Contact">
                <p style={{ fontSize: '0.875rem' }}>{form.business_email || 'No email'}</p>
                <p style={{ fontSize: '0.875rem' }}>{form.business_phone || 'No phone'}</p>
              </ReviewSection>
            </div>
          </div>
        )}

        {/* STEP 6: Subscription Selection */}
        {step === 6 && (
          <div data-testid="step-6-subscription">
            <h2>Choose Your Plan</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '8px' }}>
              Subscribe to start receiving quote leads from customers.
            </p>
            <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '20px', fontSize: '0.875rem', color: '#065F46', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <CheckCircle size={16} style={{ flexShrink: 0 }} />
              <span><strong>6-month free trial</strong> included. Your card will be collected now but you won't be charged until the trial ends.</span>
            </div>
            <div className="subscription-cards">
              <div
                className={`subscription-card ${form.subscription_plan === 'monthly' ? 'current' : ''}`}
                onClick={() => update('subscription_plan', 'monthly')}
                style={{ cursor: 'pointer', position: 'relative' }}
                data-testid="plan-monthly"
              >
                {form.subscription_plan === 'monthly' && (
                  <div style={{ position: 'absolute', top: '10px', right: '10px' }}><CheckCircle size={20} style={{ color: 'var(--accent)' }} /></div>
                )}
                <h3>Monthly</h3>
                <div className="price">{country.currencyPrefix}49<span> + {country.gstLabel} / month</span></div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', margin: '2px 0' }}>{country.gstLabel} calculated at checkout</p>
                <p style={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.8125rem', margin: '6px 0' }}>6-month free trial</p>
                <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0', textAlign: 'left' }}>
                  {['Unlimited quote leads', 'Business profile page', 'Customer messaging', 'Basic analytics'].map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                      <CheckCircle size={14} style={{ color: 'var(--success)', flexShrink: 0 }} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
              <div
                className={`subscription-card ${form.subscription_plan === 'yearly' ? 'current' : ''}`}
                onClick={() => update('subscription_plan', 'yearly')}
                style={{ cursor: 'pointer', position: 'relative' }}
                data-testid="plan-yearly"
              >
                <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', padding: '4px 14px', background: 'var(--accent)', color: 'var(--navy-start)', borderRadius: '50px', fontSize: '0.6875rem', fontWeight: 700 }}>Best Value</div>
                {form.subscription_plan === 'yearly' && (
                  <div style={{ position: 'absolute', top: '10px', right: '10px' }}><CheckCircle size={20} style={{ color: 'var(--accent)' }} /></div>
                )}
                <h3>Yearly</h3>
                <div className="price">{country.currencyPrefix}{country.yearlyPrice}<span> + {country.gstLabel} / year</span></div>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', margin: '2px 0' }}>{country.gstLabel} calculated at checkout</p>
                <div style={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.8125rem' }}>{country.yearlySavingsLabel} + 6-month free trial</div>
                <ul style={{ listStyle: 'none', padding: 0, margin: '14px 0', textAlign: 'left' }}>
                  {['Everything in Monthly', 'Priority listing', 'Featured provider badge', 'Advanced analytics'].map(f => (
                    <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '5px 0', fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                      <CheckCircle size={14} style={{ color: 'var(--success)', flexShrink: 0 }} /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* STEP 7: Final Submit via Stripe */}
        {step === 7 && (
          <div data-testid="step-7-submit" style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={{ width: '56px', height: '56px', margin: '0 auto 18px', background: 'rgba(245,158,11,0.1)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CreditCard size={28} style={{ color: 'var(--accent)' }} />
            </div>
            <h2 style={{ textAlign: 'center' }}>Complete Your Subscription</h2>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', maxWidth: '480px', margin: '0 auto 8px', lineHeight: 1.6 }}>
              You'll be redirected to Stripe to securely enter your card details.
            </p>
            <p style={{ color: 'var(--success)', fontSize: '0.875rem', fontWeight: 600, marginBottom: '24px' }}>
              Your 6-month free trial starts now. You won't be charged until the trial ends.
            </p>

            <div style={{ background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', padding: '16px', textAlign: 'left', marginBottom: '24px', maxWidth: '440px', margin: '0 auto 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Plan</span>
                <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{form.subscription_plan} ({form.subscription_plan === 'monthly' ? `${country.currencyPrefix}49 + ${country.gstLabel}/mo` : `${country.currencyPrefix}${country.yearlyPrice} + ${country.gstLabel}/yr`})</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border-color)' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Free trial</span>
                <span style={{ fontWeight: 600, color: 'var(--success)' }}>6 months</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                <span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Due today</span>
                <span style={{ fontWeight: 700, fontSize: '1.125rem' }}>{country.currencySymbol}0.00</span>
              </div>
            </div>

            <button className="btn-primary" onClick={handleFinalSubmit} disabled={loading} style={{ fontSize: '1rem', padding: '14px 32px' }} data-testid="final-submit-btn">
              {loading ? 'Redirecting to Stripe...' : 'Start Free Trial & Pay via Stripe'}
            </button>

            <div style={{ marginTop: '18px' }}>
              <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>
                Your card will be collected securely by Stripe. After the 6-month trial,
                you'll be charged {form.subscription_plan === 'monthly' ? `${country.currencyPrefix}49 + ${country.gstLabel}/month` : `${country.currencyPrefix}${country.yearlyPrice} + ${country.gstLabel}/year`} automatically. {country.gstLabel} is calculated by Stripe at checkout based on your billing address.
              </p>
            </div>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="onboarding-actions">
          {step > 1 ? (
            <button className="btn-outline" onClick={() => setStep(step - 1)} data-testid="back-btn">
              <ArrowLeft size={16} /> Back
            </button>
          ) : <div />}

          {step < 7 && (
            <button className="btn-primary" onClick={goToNextStep} disabled={!canProceed()} data-testid="next-btn">
              Continue <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Review Section Component
const ReviewSection = ({ title, children }) => (
  <div style={{ padding: '16px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)' }}>
    <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>{title}</h4>
    {children}
  </div>
);

// ============================================
// Provider Dashboard Overview
// ============================================
export const ProviderDashboardPage = () => {
  const { session } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }
    fetchDashboard();
  }, [session]);

  const fetchDashboard = async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/provider/dashboard`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) setDashboard(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="loading">Loading dashboard...</div>;
  if (!dashboard) return (
    <div className="empty-state" data-testid="no-provider-prompt">
      <p>Complete onboarding to access your provider dashboard.</p>
      <Link to="/provider/onboarding" className="btn-primary" data-testid="start-onboarding-btn">Start Onboarding</Link>
    </div>
  );

  const { stats, subscription, recent_leads, provider } = dashboard;

  return (
    <div data-testid="provider-dashboard">
      <div className="provider-page-header">
        <h1>Dashboard</h1>
        <p>Welcome back, {provider?.business_name}</p>
      </div>

      {provider?.status === 'pending' && (
        <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: '20px', fontSize: '0.875rem', color: '#92400E', display: 'flex', alignItems: 'center', gap: '10px' }} data-testid="pending-notice">
          <Clock size={18} style={{ flexShrink: 0 }} />
          <div><strong>Pending Admin Review:</strong> Your profile is being reviewed by our team. You'll receive leads once approved and become visible to customers.</div>
        </div>
      )}

      {provider?.status === 'approved' && (
        <div style={{ background: '#ECFDF5', border: '1px solid #A7F3D0', borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: '20px', fontSize: '0.875rem', color: '#065F46', display: 'flex', alignItems: 'center', gap: '10px' }} data-testid="approved-notice">
          <CheckCircle size={18} style={{ flexShrink: 0 }} />
          <div><strong>Profile Approved:</strong> Your business is live and visible to customers in your service area.</div>
        </div>
      )}

      {provider?.status === 'approved' && dashboard.completion && !dashboard.completion.has_id_verification && (
        <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: '20px', fontSize: '0.875rem', color: '#92400E', display: 'flex', alignItems: 'center', gap: '12px' }} data-testid="id-upload-prompt">
          <Clock size={18} style={{ flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <strong>Verification pending.</strong> Your public profile currently shows a <em>Verification Pending</em> badge instead of <em>Verified Provider</em>. Upload your ID/business documents so our team can confirm and unlock the green Verified badge customers trust.
          </div>
          <Link to="/provider/documents" className="btn-secondary" style={{ flexShrink: 0, padding: '8px 14px', fontSize: '0.8125rem' }} data-testid="id-upload-cta">Upload Documents</Link>
        </div>
      )}

      {provider?.status === 'rejected' && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: '20px', fontSize: '0.875rem', color: '#DC2626', display: 'flex', alignItems: 'center', gap: '10px' }} data-testid="rejected-notice">
          <XCircle size={18} style={{ flexShrink: 0 }} />
          <div><strong>Profile Rejected:</strong> Please review your information and contact support for next steps.</div>
        </div>
      )}

      {/* Profile Completion Widget */}
      {dashboard.completion && (
        <div className="dashboard-card" style={{ marginBottom: '20px' }} data-testid="profile-completion-widget">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Profile Strength</h3>
            <span style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--accent)' }} data-testid="completion-percentage">{(() => {
              const c = dashboard.completion;
              const items = [c.has_business_info, c.has_service_area, c.services_count > 0, c.has_id_verification, c.has_profile_photo, c.has_subscription];
              return Math.round((items.filter(Boolean).length / items.length) * 100);
            })()}% Complete</span>
          </div>
          <div style={{ width: '100%', height: '8px', background: 'var(--surface-hover)', borderRadius: '4px', overflow: 'hidden', marginBottom: '16px' }}>
            <div data-testid="completion-bar" style={{ height: '100%', background: 'var(--accent)', borderRadius: '4px', transition: 'width 0.5s ease', width: `${(() => {
              const c = dashboard.completion;
              const items = [c.has_business_info, c.has_service_area, c.services_count > 0, c.has_id_verification, c.has_profile_photo, c.has_subscription];
              return Math.round((items.filter(Boolean).length / items.length) * 100);
            })()}%` }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { done: dashboard.completion.has_business_info, label: 'Business Information', link: '/provider/profile#business-info' },
              { done: dashboard.completion.has_service_area, label: 'Service Area', link: '/provider/profile#service-area' },
              { done: dashboard.completion.services_count > 0, label: 'Services Added', link: '/provider/services' },
              { done: dashboard.completion.has_id_verification, label: 'Upload Identity Verification', link: '/provider/documents' },
              { done: dashboard.completion.has_profile_photo, label: 'Add Profile Photo', link: '/provider/profile#profile-photo' },
              { done: dashboard.completion.has_subscription, label: 'Activate Subscription', link: '/provider/subscription' },
            ].map((item, i) => (
              <Link key={i} to={item.link} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: item.done ? 'rgba(34,197,94,0.06)' : 'var(--surface-hover)', borderRadius: 'var(--radius-sm)', textDecoration: 'none', color: 'inherit', fontSize: '0.875rem', transition: 'background 0.2s' }} data-testid={`completion-${item.label.toLowerCase().replace(/\s/g, '-')}`}>
                {item.done ? <CheckCircle size={16} style={{ color: 'var(--success)', flexShrink: 0 }} /> : <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: '2px solid var(--border-color)', flexShrink: 0 }} />}
                <span style={{ fontWeight: item.done ? 400 : 500, color: item.done ? 'var(--text-muted)' : 'var(--text-dark)', textDecoration: item.done ? 'line-through' : 'none' }}>{item.label}</span>
                {!item.done && <ChevronRight size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)' }} />}
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="dashboard-stats">
        <div className="stat-card" data-testid="stat-leads"><div className="stat-card-header"><span>Quote Leads</span><Users size={20} /></div><div className="stat-card-value">{stats.total_leads}</div></div>
        <div className="stat-card" data-testid="stat-responses"><div className="stat-card-header"><span>Responses Sent</span><Send size={20} /></div><div className="stat-card-value">{stats.responses_sent}</div></div>
        <div className="stat-card" data-testid="stat-jobs"><div className="stat-card-header"><span>Jobs Done</span><CheckCircle size={20} /></div><div className="stat-card-value">{stats.jobs_completed}</div></div>
        <div className="stat-card" data-testid="stat-rating"><div className="stat-card-header"><span>Rating</span><Star size={20} /></div><div className="stat-card-value">{stats.average_rating?.toFixed(1) || 'N/A'}</div></div>
      </div>

      <div className="dashboard-grid">
        <div className="dashboard-card">
          <div className="dashboard-card-header"><h3>Recent Quote Leads</h3><Link to="/provider/quotes">View All</Link></div>
          {recent_leads.length > 0 ? recent_leads.map(lead => (
            <div key={lead.id} className="quote-lead-item" data-testid={`lead-${lead.id}`}>
              <div className="quote-lead-header"><h4>{lead.service_name}</h4><span className={`status-badge status-${lead.status}`}>{lead.status}</span></div>
              <p className="quote-lead-desc">{lead.job_description}</p>
              <div className="quote-lead-meta">
                <span><MapPin size={12} /> {lead.city}, {lead.country_code}</span>
                {lead.preferred_date && <span><Calendar size={12} /> {lead.preferred_date}</span>}
                <span><Clock size={12} /> {formatDateNZ(lead.created_at)}</span>
              </div>
            </div>
          )) : <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', padding: '20px 0' }}>No quote leads yet.</p>}
        </div>

        <div>
          <div className="dashboard-card" style={{ marginBottom: '16px' }}>
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '14px' }}>Subscription</h3>
            {subscription && ['active', 'trial'].includes(subscription.status) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Plan</span><span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{subscription.plan}</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>Status</span><span className={`status-badge ${subscription.status === 'active' || subscription.status === 'trial' ? 'status-completed' : 'status-submitted'}`}>{subscription.status === 'trial' ? 'Trial Active' : subscription.status}</span></div>
                <Link to="/provider/subscription" className="btn-outline" data-testid="dashboard-manage-billing-link" style={{ fontSize: '0.8125rem', justifyContent: 'center', marginTop: '6px' }}>
                  <CreditCard size={13} /> Manage Billing
                </Link>
              </div>
            ) : (
              <div><p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '12px' }}>No active subscription</p><Link to="/provider/subscription" className="btn-primary" style={{ fontSize: '0.8125rem' }}>Subscribe Now</Link></div>
            )}
          </div>
          <div className="dashboard-card">
            <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '14px' }}>Quick Actions</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/provider/profile" className="btn-outline" style={{ justifyContent: 'space-between', display: 'flex' }}>Edit Profile <ChevronRight size={16} /></Link>
              <Link to="/provider/services" className="btn-outline" style={{ justifyContent: 'space-between', display: 'flex' }}>Manage Services <ChevronRight size={16} /></Link>
              <Link to="/provider/documents" className="btn-outline" style={{ justifyContent: 'space-between', display: 'flex' }}>Upload Documents <ChevronRight size={16} /></Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Provider Profile Edit
// ============================================
export const ProviderProfilePage = () => {
  const { session, profile, refreshProfile } = useAuth();
  const { country, showToggle } = useCountry();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [form, setForm] = useState({});
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState('');

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const res = await fetch(`${API_URL}/provider/profile`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setProvider(data);
        setForm({
          business_name: data.business_name || '',
          business_email: data.business_email || '',
          business_phone: data.business_phone || '',
          bio: data.bio || '',
          years_experience: data.years_experience || '',
          hourly_rate_min: data.hourly_rate_min || '',
          hourly_rate_max: data.hourly_rate_max || '',
          country_code: data.country_code || 'NZ',
          city: data.city || '',
          suburbs_served: data.suburbs_served || [],
          service_radius_km: data.service_radius_km || 25,
          availability: data.availability || {},
          weekend_available: data.weekend_available || false,
          same_day_available: data.same_day_available || false,
        });
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handlePhotoChoose = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.jpg,.jpeg,.png,.webp';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const allowed = ['jpg', 'jpeg', 'png', 'webp'];
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!allowed.includes(ext)) { setPhotoError(`Unsupported file type. Allowed: ${allowed.join(', ')}.`); return; }
      if (file.size > 5 * 1024 * 1024) { setPhotoError(`Image is ${(file.size / 1024 / 1024).toFixed(1)} MB — must be smaller than 5 MB.`); return; }
      if (file.size === 0) { setPhotoError('File is empty.'); return; }

      setPhotoUploading(true);
      setPhotoError('');

      const fd = new FormData();
      fd.append('file', file);

      let res;
      try {
        res = await fetch(`${API_URL}/provider/profile-photo`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session.access_token}` },
          body: fd,
        });
      } catch (netErr) {
        setPhotoError(`Network error: ${netErr?.message || 'unknown'}.`);
        setPhotoUploading(false);
        return;
      }
      let text = ''; try { text = await res.text(); } catch { text = ''; }
      let body = null; try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }
      if (res.ok && body?.avatar_url) {
        // Refresh AuthContext profile so the avatar updates immediately everywhere it's read.
        try { await refreshProfile?.(); } catch {}
        setSaveMsg('Profile photo updated.');
        setPhotoSuccess('Uploaded successfully — photo is now visible across HandyHub.');
        setTimeout(() => { setSaveMsg(''); setPhotoSuccess(''); }, 4000);
      } else {
        setPhotoError(body?.detail || `Photo upload failed (HTTP ${res.status}).`);
      }
      setPhotoUploading(false);
    };
    input.click();
  };

  const handlePhotoRemove = async () => {
    if (!window.confirm('Remove your profile photo?')) return;
    setPhotoUploading(true);
    setPhotoError('');
    try {
      const res = await fetch(`${API_URL}/provider/profile-photo`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      if (res.ok) {
        try { await refreshProfile?.(); } catch {}
        setSaveMsg('Profile photo removed.');
        setTimeout(() => setSaveMsg(''), 3000);
      } else {
        let body = null; try { body = await res.json(); } catch {}
        setPhotoError(body?.detail || 'Could not remove photo.');
      }
    } finally { setPhotoUploading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/provider/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({
          ...form,
          years_experience: form.years_experience ? parseInt(form.years_experience) : null,
          hourly_rate_min: form.hourly_rate_min ? parseFloat(form.hourly_rate_min) : null,
          hourly_rate_max: form.hourly_rate_max ? parseFloat(form.hourly_rate_max) : null
        })
      });
      if (res.ok) { setForm({ ...form, ...await res.json() }); setSaveMsg('Profile saved successfully.'); setTimeout(() => setSaveMsg(''), 3000); }
      else { setSaveMsg('Failed to save. Please try again.'); }
    } catch (err) { console.error('Failed to update'); }
    finally { setSaving(false); }
  };

  // Scroll to anchored section when arriving from dashboard shortcut links (e.g. /provider/profile#insurance)
  const location = useLocation();
  useEffect(() => {
    if (loading) return;
    const hash = location.hash;
    if (!hash) return;
    const id = hash.slice(1);
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [loading, location.hash]);

  if (loading) return <div className="loading">Loading...</div>;

  const cityList = (form.country_code === 'NZ' ? CITIES_NZ : CITIES_AU);

  return (
    <div data-testid="provider-profile-page">
      <div className="provider-page-header"><h1>Business Profile</h1><p>Manage your business information</p></div>

      {/* Profile photo block */}
      <div id="profile-photo" className="dashboard-card" style={{ marginBottom: '20px', scrollMarginTop: '80px' }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '4px' }}>Profile Photo</h3>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem', marginBottom: '14px' }}>
          A clear photo of you or your business helps customers recognise and trust your profile.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '18px', flexWrap: 'wrap' }}>
          <div style={{ width: 88, height: 88, borderRadius: '50%', background: profile?.avatar_url ? `url(${profile.avatar_url}) center/cover no-repeat` : 'rgba(15,23,42,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, border: '1px solid var(--border-color)' }}>
            {!profile?.avatar_url && <Camera size={28} style={{ color: 'var(--text-muted)' }} />}
          </div>
          <div style={{ flex: 1, minWidth: '220px' }}>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                className="btn-primary"
                onClick={handlePhotoChoose}
                disabled={photoUploading}
                data-testid="profile-photo-upload-btn"
                style={{ fontSize: '0.8125rem' }}
              >
                {photoUploading ? (
                  <><span className="upload-spinner" /> Uploading…</>
                ) : (
                  <><Upload size={14} /> {profile?.avatar_url ? 'Replace Photo' : 'Add Profile Photo'}</>
                )}
              </button>
              {profile?.avatar_url && (
                <button
                  type="button"
                  className="btn-outline"
                  onClick={handlePhotoRemove}
                  disabled={photoUploading}
                  data-testid="profile-photo-remove-btn"
                  style={{ fontSize: '0.8125rem' }}
                >
                  <Trash2 size={14} /> Remove
                </button>
              )}
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '8px' }}>
              JPG, PNG, or WebP. Max 5 MB.
            </p>
            {photoError && (
              <div className="inline-alert inline-alert-error" role="alert" data-testid="profile-photo-error" style={{ marginTop: '8px' }}>
                <AlertCircle size={14} /> {photoError}
              </div>
            )}
            {photoSuccess && (
              <div className="inline-alert inline-alert-success" role="status" data-testid="profile-photo-success" style={{ marginTop: '8px' }}>
                <CheckCircle size={14} /> {photoSuccess}
              </div>
            )}
          </div>
        </div>
      </div>

      <div id="business-info" className="dashboard-card" style={{ scrollMarginTop: '80px' }}>
        <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '12px' }}>Business Information & Service Area</h3>
        <div className="profile-form-grid">
          <div className="form-group"><label>Business Name</label><input type="text" value={form.business_name || ''} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="edit-business-name" /></div>
          <div className="form-group"><label>Business Email</label><input type="email" value={form.business_email || ''} onChange={(e) => setForm({ ...form, business_email: e.target.value })} data-testid="edit-business-email" /></div>
          <div className="form-group"><label>Phone</label><input type="tel" value={form.business_phone || ''} placeholder={country.phonePlaceholder} onChange={(e) => setForm({ ...form, business_phone: e.target.value })} data-testid="edit-business-phone" /></div>
          <div className="form-group"><label>Years of Experience</label><input type="number" value={form.years_experience || ''} onChange={(e) => setForm({ ...form, years_experience: e.target.value })} data-testid="edit-years-exp" /></div>
          {showToggle ? (
            <div id="service-area" className="form-group" style={{ scrollMarginTop: '80px' }}><label>Country</label><select value={form.country_code || 'NZ'} onChange={(e) => setForm({ ...form, country_code: e.target.value, city: '' })} data-testid="edit-country"><option value="NZ">New Zealand</option><option value="AU">Australia</option></select></div>
          ) : (
            <div id="service-area" className="form-group" style={{ scrollMarginTop: '80px' }}><label>Country</label><input type="text" value={form.country_code === 'AU' ? 'Australia' : 'New Zealand'} readOnly disabled data-testid="edit-country-locked" style={{ background: 'var(--surface-hover)', cursor: 'not-allowed' }} /></div>
          )}
          <div className="form-group"><label>City</label><select value={form.city || ''} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="edit-city"><option value="">Select city</option>{cityList.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
          <div className="form-group full-width">
            <label>Service Radius: {form.service_radius_km || 25} km</label>
            <input type="range" min="5" max="100" step="5" value={form.service_radius_km || 25} onChange={(e) => setForm({ ...form, service_radius_km: parseInt(e.target.value) })} data-testid="edit-radius" />
          </div>
          <div className="form-group full-width"><label>Hourly Rate Range ($)</label><div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}><input type="number" value={form.hourly_rate_min || ''} placeholder="Min" onChange={(e) => setForm({ ...form, hourly_rate_min: e.target.value })} style={{ flex: 1 }} /><span style={{ color: 'var(--text-muted)' }}>to</span><input type="number" value={form.hourly_rate_max || ''} placeholder="Max" onChange={(e) => setForm({ ...form, hourly_rate_max: e.target.value })} style={{ flex: 1 }} /></div></div>
          <div className="form-group full-width"><label>About Your Business</label><textarea value={form.bio || ''} rows={4} onChange={(e) => setForm({ ...form, bio: e.target.value })} data-testid="edit-bio" /></div>
          <div className="form-group full-width" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <label className={`service-select-item ${form.same_day_available ? 'selected' : ''}`}><input type="checkbox" checked={form.same_day_available || false} onChange={(e) => setForm({ ...form, same_day_available: e.target.checked })} /><span>Same-day availability</span></label>
            <label className={`service-select-item ${form.weekend_available ? 'selected' : ''}`}><input type="checkbox" checked={form.weekend_available || false} onChange={(e) => setForm({ ...form, weekend_available: e.target.checked })} /><span>Weekend availability</span></label>
          </div>
        </div>
        <div style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '14px' }}>
          <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-profile-btn">{saving ? 'Saving...' : 'Save Changes'}</button>
          {saveMsg && <span style={{ fontSize: '0.8125rem', color: saveMsg.includes('success') ? 'var(--success)' : '#DC2626' }}>{saveMsg}</span>}
        </div>
      </div>
    </div>
  );
};

// ============================================
// Provider Services Management
// ============================================
export const ProviderServicesPage = () => {
  const { session } = useAuth();
  const [allServices, setAllServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [originalIds, setOriginalIds] = useState([]); // snapshot of last saved state
  const [serviceRequests, setServiceRequests] = useState([]);
  const [saveMsg, setSaveMsg] = useState('');
  const [activeCategoryId, setActiveCategoryId] = useState(''); // '' = all

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [myRes, allRes, catRes, reqRes] = await Promise.all([
        fetch(`${API_URL}/provider/services`, { headers: { 'Authorization': `Bearer ${session.access_token}` } }),
        fetch(`${API_URL}/services`),
        fetch(`${API_URL}/categories`),
        fetch(`${API_URL}/provider/service-requests`, { headers: { 'Authorization': `Bearer ${session.access_token}` } })
      ]);
      const my = myRes.ok ? await myRes.json() : [];
      const myIds = Array.isArray(my) ? my.filter(s => s.is_active !== false).map(s => s.id) : [];
      setSelectedIds(myIds);
      setOriginalIds(myIds);
      setAllServices(await allRes.json());
      setCategories(await catRes.json());
      if (reqRes.ok) setServiceRequests(await reqRes.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`${API_URL}/provider/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ service_ids: selectedIds })
      });
      if (res.ok) {
        setOriginalIds([...selectedIds]);  // commit new baseline
        setSaveMsg('Services updated!');
        setTimeout(() => setSaveMsg(''), 3500);
      } else {
        setSaveMsg('Save failed — please try again.');
      }
    } catch (err) {
      console.error(err);
      setSaveMsg('Network error — please try again.');
    }
    finally { setSaving(false); }
  };

  if (loading) return <div className="loading">Loading...</div>;

  // Build category list (only those with services) and apply the active filter
  const servicesByCategory = categories.map(cat => ({
    ...cat,
    services: allServices.filter(s => s.category_id === cat.id)
  })).filter(cat => cat.services.length > 0);

  const visibleCategories = activeCategoryId
    ? servicesByCategory.filter(c => c.id === activeCategoryId)
    : servicesByCategory;

  // Dirty = selection differs from last saved state (used for sticky-bar visibility)
  const isDirty = (() => {
    if (selectedIds.length !== originalIds.length) return true;
    const sel = new Set(selectedIds);
    return originalIds.some(id => !sel.has(id));
  })();

  return (
    <div data-testid="provider-services-page">
      <div className="provider-page-header">
        <h1>My Services</h1>
        <p>Select the services you offer ({selectedIds.length} selected)</p>
      </div>

      <div className="dashboard-card">
        {/* Category dropdown filter */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <label htmlFor="cat-filter" style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-dark)' }}>
            Filter by category:
          </label>
          <select
            id="cat-filter"
            data-testid="services-category-filter"
            value={activeCategoryId}
            onChange={e => setActiveCategoryId(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border-color)', fontSize: '0.875rem', minWidth: '220px', background: '#fff' }}
          >
            <option value="">All categories ({servicesByCategory.length})</option>
            {servicesByCategory.map(c => {
              const selectedInCat = c.services.filter(s => selectedIds.includes(s.id)).length;
              return (
                <option key={c.id} value={c.id}>
                  {c.name}{selectedInCat > 0 ? ` — ${selectedInCat} selected` : ''}
                </option>
              );
            })}
          </select>
          <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
            <strong data-testid="services-selected-count">{selectedIds.length}</strong> service{selectedIds.length === 1 ? '' : 's'} selected total
          </span>
        </div>

        {visibleCategories.length === 0 && (
          <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', padding: '16px 0' }}>
            No services in this category yet.
          </p>
        )}

        {visibleCategories.map(cat => {
          const selectedInCat = cat.services.filter(s => selectedIds.includes(s.id)).length;
          return (
            <div key={cat.id} style={{ marginBottom: '24px' }} data-testid={`services-cat-block-${cat.slug}`}>
              <h3 style={{ fontSize: '0.9375rem', fontWeight: 600, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: 8 }}>
                {cat.name}
                {selectedInCat > 0 && (
                  <span style={{ padding: '2px 8px', background: 'rgba(255,107,53,0.12)', color: 'var(--accent)', borderRadius: '50px', fontSize: '0.6875rem', fontWeight: 700 }}>
                    {selectedInCat} selected
                  </span>
                )}
              </h3>
              <div className="service-select-grid">
                {cat.services.map(svc => (
                  <label key={svc.id} className={`service-select-item ${selectedIds.includes(svc.id) ? 'selected' : ''}`} data-testid={`svc-${svc.slug}`}>
                    <input type="checkbox" checked={selectedIds.includes(svc.id)} onChange={() => setSelectedIds(prev => prev.includes(svc.id) ? prev.filter(x => x !== svc.id) : [...prev, svc.id])} />
                    <span>{svc.name}</span>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        {/* Inline save button (still functional even if sticky is hidden) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', borderTop: '1px solid var(--border-color)', paddingTop: 16, marginTop: 16 }}>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving || !isDirty}
            data-testid="save-services-btn"
          >
            {saving ? 'Saving...' : 'Save Services'}
          </button>
          {!isDirty && !saving && <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>All changes saved</span>}
          {saveMsg && <span style={{ fontSize: '0.8125rem', color: saveMsg.includes('fail') || saveMsg.includes('error') ? '#DC2626' : 'var(--success)' }}>{saveMsg}</span>}
        </div>
      </div>

      {/* Sticky / floating save bar — appears as soon as the selection is dirty */}
      {isDirty && (
        <div className="sticky-save-bar" data-testid="sticky-save-bar" role="region" aria-label="Unsaved service changes">
          <span className="sticky-save-bar__msg">
            <strong>{selectedIds.length}</strong> service{selectedIds.length === 1 ? '' : 's'} selected — you have unsaved changes
          </span>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={saving}
            data-testid="sticky-save-services-btn"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      )}

      {serviceRequests.length > 0 && (
        <div className="dashboard-card" style={{ marginTop: '20px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '14px' }}>Your Service Requests</h3>
          {serviceRequests.map(sr => (
            <div key={sr.id} style={{ padding: '10px 0', borderBottom: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div><p style={{ fontWeight: 500, fontSize: '0.875rem' }}>{sr.name}</p><p style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{sr.category_name}</p></div>
              <span className={`status-badge ${sr.status === 'approved' ? 'status-completed' : sr.status === 'rejected' ? 'status-cancelled' : 'status-submitted'}`}>{sr.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Provider Quote Leads
// ============================================
export const ProviderQuotesPage = () => {
  const { session } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondingTo, setRespondingTo] = useState(null);
  const [responseForm, setResponseForm] = useState({ message: '', proposed_price: '', estimated_duration: '' });
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => { if (session?.access_token) fetchLeads(); }, [session]);

  const fetchLeads = async () => {
    if (!session?.access_token) return;
    try {
      const res = await fetch(`${API_URL}/provider/quotes`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.ok) setLeads(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleRespond = async (quoteRequestId) => {
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/provider/quotes/${quoteRequestId}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body: JSON.stringify({ quote_request_id: quoteRequestId, message: responseForm.message, proposed_price: responseForm.proposed_price ? parseFloat(responseForm.proposed_price) : null, estimated_duration: responseForm.estimated_duration || null })
      });
      if (res.ok) { setRespondingTo(null); setResponseForm({ message: '', proposed_price: '', estimated_duration: '' }); fetchLeads(); }
      else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Failed to send response.');
      }
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const handleDecline = async (quoteRequestId) => {
    if (!window.confirm('Are you sure you want to decline this lead? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_URL}/provider/quotes/${quoteRequestId}/decline`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${session.access_token}` },
      });
      if (res.ok) fetchLeads();
    } catch (err) { console.error(err); }
  };

  const filteredLeads = filter === 'all' ? leads : leads.filter(l => l.status === filter);
  const counts = { all: leads.length, pending: leads.filter(l => l.status === 'pending').length, responded: leads.filter(l => l.status === 'responded').length };

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div data-testid="provider-quotes-page">
      <div className="provider-page-header"><h1>Quote Leads</h1><p>View and respond to customer quote requests</p></div>
      
      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {[
          { key: 'all', label: `All (${counts.all})` },
          { key: 'pending', label: `New (${counts.pending})` },
          { key: 'responded', label: `Responded (${counts.responded})` },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? 'btn-primary' : 'btn-outline'}
            style={{ fontSize: '0.8125rem', padding: '6px 14px' }}
            data-testid={`filter-${f.key}`}
          >{f.label}</button>
        ))}
      </div>

      {filteredLeads.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {filteredLeads.map(lead => (
            <div key={lead.id} className="dashboard-card" data-testid={`quote-lead-${lead.id}`} style={{ borderLeft: `3px solid ${lead.status === 'pending' ? '#f59e0b' : lead.status === 'responded' ? '#10b981' : '#9ca3af'}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div><h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '2px' }}>{lead.service_name}</h3><p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>from {lead.customer_name}</p></div>
                <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: '0.7rem', fontWeight: 600, 
                  color: lead.status === 'pending' ? '#92400e' : lead.status === 'responded' ? '#065f46' : '#6b7280', 
                  background: lead.status === 'pending' ? '#fef3c7' : lead.status === 'responded' ? '#d1fae5' : '#f3f4f6' 
                }}>{lead.status === 'pending' ? 'New Lead' : lead.status === 'responded' ? 'Responded' : 'Declined'}</span>
              </div>
              <p style={{ color: 'var(--text-dark)', fontSize: '0.875rem', marginBottom: '12px', lineHeight: 1.6 }}>{lead.job_description}</p>
              <div className="quote-lead-meta" style={{ marginBottom: '14px' }}>
                <span><MapPin size={12} /> {lead.city}{lead.suburb ? `, ${lead.suburb}` : ''}, {lead.country_code}</span>
                {lead.preferred_date && <span><Calendar size={12} /> {lead.preferred_date}</span>}
                {lead.preferred_time && <span><Clock size={12} /> {lead.preferred_time}</span>}
              </div>
              {lead.status === 'pending' && respondingTo !== lead.quote_request_id && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn-primary" onClick={() => setRespondingTo(lead.quote_request_id)} style={{ fontSize: '0.8125rem' }} data-testid={`respond-btn-${lead.id}`}><Send size={14} /> Respond</button>
                  <button className="btn-outline" onClick={() => handleDecline(lead.quote_request_id)} style={{ fontSize: '0.8125rem', color: '#991b1b', borderColor: '#fecaca' }} data-testid={`decline-btn-${lead.id}`}>Decline</button>
                </div>
              )}
              {respondingTo === lead.quote_request_id && (
                <div style={{ marginTop: '14px', padding: '18px', background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)' }} data-testid={`response-form-${lead.id}`}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '12px' }}>Your Response</h4>
                  <div className="form-group"><label>Message *</label><textarea value={responseForm.message} onChange={(e) => setResponseForm({ ...responseForm, message: e.target.value })} placeholder="Introduce yourself and explain how you can help..." rows={3} data-testid="response-message" /></div>
                  <div style={{ display: 'flex', gap: '12px', marginBottom: '14px' }}>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}><label>Price ($)</label><input type="number" value={responseForm.proposed_price} onChange={(e) => setResponseForm({ ...responseForm, proposed_price: e.target.value })} placeholder="e.g. 150" data-testid="response-price" /></div>
                    <div className="form-group" style={{ flex: 1, marginBottom: 0 }}><label>Duration</label><input type="text" value={responseForm.estimated_duration} onChange={(e) => setResponseForm({ ...responseForm, estimated_duration: e.target.value })} placeholder="e.g. 2-3 hours" data-testid="response-duration" /></div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button className="btn-primary" onClick={() => handleRespond(lead.quote_request_id)} disabled={sending || !responseForm.message} data-testid="send-response-btn">{sending ? 'Sending...' : 'Send Response'}</button>
                    <button className="btn-outline" onClick={() => setRespondingTo(null)}>Cancel</button>
                  </div>
                </div>
              )}
              {lead.status === 'responded' && <p style={{ color: 'var(--success)', fontSize: '0.8125rem', fontWeight: 500 }}><CheckCircle size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} />You responded to this lead</p>}
            </div>
          ))}
        </div>
      ) : <div className="empty-state"><p>{filter === 'all' ? "No quote leads yet. They'll appear when customers request your services." : `No ${filter} leads.`}</p></div>}
    </div>
  );
};

// ============================================
// Provider Documents / Verification
// ============================================
export const ProviderDocumentsPage = () => {
  const { session } = useAuth();
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  useEffect(() => { fetchDocuments(); }, []);

  const fetchDocuments = async () => {
    try {
      const res = await fetch(`${API_URL}/provider/documents`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.ok) {
        setDocuments(await res.json());
      } else if (res.status === 404) {
        // Provider row missing — user hasn't completed onboarding yet
        setNeedsOnboarding(true);
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleUpload = async (docType) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.pdf,.jpg,.jpeg,.png';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      // Client-side validation
      const MAX_SIZE_MB = 10;
      const allowedExts = ['pdf', 'jpg', 'jpeg', 'png'];
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      if (!allowedExts.includes(ext)) {
        setUploadError(`"${file.name}" — unsupported file type. Please upload a PDF, JPG, or PNG.`);
        return;
      }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(1);
        setUploadError(`"${file.name}" is ${sizeMb} MB — files must be smaller than ${MAX_SIZE_MB} MB.`);
        return;
      }
      if (file.size === 0) {
        setUploadError(`"${file.name}" appears to be empty (0 bytes).`);
        return;
      }

      setUploading(true);
      setUploadError('');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', docType);

      console.log(`[DocUpload] Uploading ${docType}: ${file.name} (${file.type}, ${(file.size/1024).toFixed(1)} KB)`);

      // Step 1 — only true network failures reach the catch
      let res;
      try {
        res = await fetch(`${API_URL}/provider/documents`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${session.access_token}` },
          body: fd
        });
      } catch (networkErr) {
        console.error('[DocUpload] Network error:', networkErr);
        setUploadError(`Network error during upload: ${networkErr?.message || 'unknown'}.`);
        setUploading(false);
        return;
      }

      // Step 2 — read body ONCE; tolerate Emergent fetch-interceptor pre-consumption
      let text = '';
      try {
        text = await res.text();
      } catch (readErr) {
        console.warn('[DocUpload] Body already consumed by interceptor:', readErr?.message);
        text = '';
      }
      let body = null;
      try { body = text ? JSON.parse(text) : null; } catch { body = { raw: text }; }

      try {
        if (res.ok && body?.id) {
          console.log('[DocUpload] Upload SUCCESS:', body);
          fetchDocuments();
          setUploadError('');
        } else {
          const backendMsg = body?.detail || body?.message || body?.raw || '';
          console.error('[DocUpload] Upload FAILED:', res.status, body);
          if (res.status === 401) setUploadError('Your session has expired. Please refresh and log in again.');
          else if (res.status === 404) setUploadError('Provider profile not found. Complete onboarding first.');
          else if (res.status === 413) setUploadError(`File too large. Maximum upload size is 10 MB.`);
          else if (res.status === 429) setUploadError('Too many uploads — please wait a moment and try again.');
          else if (backendMsg) setUploadError(`Upload failed: ${backendMsg}`);
          else setUploadError(`Upload failed (HTTP ${res.status}). Please try again.`);
        }
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const docTypes = [
    { key: 'government_id', label: 'Government ID', desc: "Driver's licence, passport, or national ID" },
  ];

  if (loading) return <div className="loading">Loading...</div>;

  // Graceful guard: user arrived here without completing onboarding → redirect them
  // to the wizard instead of showing a raw error.
  if (needsOnboarding) {
    return (
      <div data-testid="documents-needs-onboarding" style={{ maxWidth: '640px', margin: '40px auto', padding: '28px 32px', background: 'var(--card-bg)', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-color)', textAlign: 'center' }}>
        <div style={{ width: 48, height: 48, margin: '0 auto 14px', background: 'rgba(245,158,11,0.12)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AlertCircle size={22} style={{ color: '#b45309' }} />
        </div>
        <h2 style={{ marginBottom: '10px' }}>Finish setting up your profile first</h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6, marginBottom: '18px' }}>
          You can upload verification documents once your business details, location, and services are saved. It only takes a minute.
        </p>
        <button className="btn-primary" onClick={() => navigate('/provider/onboarding')} data-testid="go-to-onboarding">
          Continue Onboarding <ArrowRight size={14} />
        </button>
      </div>
    );
  }

  return (
    <div data-testid="provider-documents-page">
      <div className="provider-page-header"><h1>Verification Documents</h1><p>Upload documents to get verified and build trust with customers</p></div>
      {uploadError && (
        <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 'var(--radius-md)', padding: '12px 16px', marginBottom: '16px', fontSize: '0.875rem', color: '#DC2626' }} data-testid="upload-error">{uploadError}</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {docTypes.map(dt => {
          const uploaded = documents.filter(d => d.document_type === dt.key);
          const latest = uploaded.length > 0 ? uploaded[0].status : null;
          return (
            <div key={dt.key} className="dashboard-card" data-testid={`doc-section-${dt.key}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div><h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '2px' }}>{dt.label}</h3><p style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{dt.desc}</p></div>
                {latest && <span className={`status-badge ${latest === 'approved' ? 'status-completed' : latest === 'rejected' ? 'status-cancelled' : 'status-submitted'}`}>{latest}</span>}
              </div>
              {uploaded.length > 0 && (
                <div className="uploaded-files">{uploaded.map(doc => (
                  <div key={doc.id} className="uploaded-file">
                    <span><FileText size={14} /> {doc.file_name || doc.document_url?.split('/').pop()}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <button
                        type="button"
                        className="doc-view-btn"
                        onClick={async () => {
                          const r = await viewProviderDocument({
                            verificationId: doc.id,
                            accessToken: session?.access_token,
                            apiUrl: API_URL,
                          });
                          if (!r.ok) setUploadError(r.error);
                        }}
                        data-testid={`view-doc-${doc.id}`}
                      >
                        <Eye size={13} /> View
                      </button>
                      {doc.status !== 'approved' && (
                        <button
                          type="button"
                          className="doc-view-btn doc-remove-btn"
                          onClick={async () => {
                            if (!window.confirm('Remove this document?')) return;
                            const res = await fetch(`${API_URL}/provider/documents/${doc.id}`, {
                              method: 'DELETE',
                              headers: { 'Authorization': `Bearer ${session.access_token}` },
                            });
                            if (res.ok) {
                              fetchDocuments();
                              setUploadError('');
                            } else {
                              let body = null; try { body = await res.json(); } catch {}
                              setUploadError(body?.detail || `Could not remove document (HTTP ${res.status}).`);
                            }
                          }}
                          data-testid={`remove-doc-${doc.id}`}
                        >
                          <Trash2 size={13} /> Remove
                        </button>
                      )}
                      <span className={`status-badge ${doc.status === 'approved' ? 'status-completed' : doc.status === 'rejected' ? 'status-cancelled' : 'status-submitted'}`} style={{ fontSize: '0.6875rem' }}>{doc.status}</span>
                    </div>
                  </div>
                ))}</div>
              )}
              <button className="btn-outline" onClick={() => handleUpload(dt.key)} disabled={uploading} style={{ marginTop: '12px', fontSize: '0.8125rem' }} data-testid={`upload-${dt.key}-btn`}>
                <Upload size={14} /> {uploaded.length > 0 ? 'Upload New' : 'Upload Document'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ============================================
// Provider Reviews Page
// ============================================
export const ProviderReviewsPage = () => {
  const { session } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (session?.access_token) fetchReviews(); }, [session]);

  const fetchReviews = async () => {
    try {
      const res = await fetch(`${API_URL}/provider/reviews`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="loading">Loading...</div>;
  if (!data) return <div className="empty-state"><p>Unable to load reviews.</p></div>;

  const { reviews } = data;
  // Derive summary from the SAME reviews dataset as the list (no cached/separate source)
  const totalReviews = reviews.length;
  const averageRating = totalReviews > 0
    ? reviews.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) / totalReviews
    : 0;

  return (
    <div data-testid="provider-reviews-page">
      <div className="provider-page-header">
        <h1>Reviews</h1>
        <p>See what your customers are saying</p>
      </div>

      {/* Summary card */}
      <div className="dashboard-card" style={{ marginBottom: '20px', display: 'flex', gap: '32px', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', fontWeight: 700, color: 'var(--text-dark)', lineHeight: 1 }} data-testid="avg-rating">
            {totalReviews > 0 ? averageRating.toFixed(1) : '—'}
          </div>
          <div style={{ display: 'flex', gap: '2px', justifyContent: 'center', margin: '6px 0' }}>
            {[1,2,3,4,5].map(i => (
              <Star key={i} size={16} fill={i <= Math.round(averageRating) ? '#f59e0b' : 'none'} stroke={i <= Math.round(averageRating) ? '#f59e0b' : '#d1d5db'} />
            ))}
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }} data-testid="review-count">{totalReviews} review{totalReviews !== 1 ? 's' : ''}</div>
        </div>
        <div style={{ flex: 1 }}>
          {[5,4,3,2,1].map(stars => {
            const count = reviews.filter(r => Number(r.rating) === stars).length;
            const pct = totalReviews > 0 ? (count / totalReviews) * 100 : 0;
            return (
              <div key={stars} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', width: '14px', textAlign: 'right' }}>{stars}</span>
                <Star size={12} fill="#f59e0b" stroke="#f59e0b" />
                <div style={{ flex: 1, height: '8px', background: 'var(--surface-hover)', borderRadius: '4px', overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#f59e0b', borderRadius: '4px' }} />
                </div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', width: '24px' }}>{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Review list */}
      {reviews.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {reviews.map(rev => (
            <div key={rev.id} className="dashboard-card" data-testid={`review-${rev.id}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--accent)', color: 'var(--navy-start)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8125rem', flexShrink: 0 }}>
                    {(rev.customer?.full_name || 'C').charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: '1px' }}>{rev.customer?.full_name || 'Customer'}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{formatDateNZ(rev.created_at)}</p>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '2px' }}>
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={14} fill={i <= rev.rating ? '#f59e0b' : 'none'} stroke={i <= rev.rating ? '#f59e0b' : '#d1d5db'} />
                  ))}
                </div>
              </div>
              <p style={{ color: 'var(--text-dark)', fontSize: '0.875rem', lineHeight: 1.6 }}>{rev.comment}</p>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <p>No reviews yet. Reviews will appear here once customers rate your work.</p>
        </div>
      )}
    </div>
  );
};

// ============================================
// Provider Subscription Page
// ============================================
export const ProviderSubscriptionPage = () => {
  const { session, acceptedTerms: prevAccepted } = useAuth();
  const { country } = useCountry();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState('');
  const [portalLoading, setPortalLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { fetchSubscription(); }, []);

  const fetchSubscription = async () => {
    try {
      const res = await fetch(`${API_URL}/provider/subscription`, { headers: { 'Authorization': `Bearer ${session.access_token}` } });
      if (res.ok) setSubscription(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleCheckout = async (plan) => {
    setError('');
    if (!prevAccepted && !acceptTerms) {
      setError('You must agree to the Terms of Service and Privacy Policy before subscribing.');
      return;
    }
    setCheckoutLoading(plan);
    try {
      const res = await fetch(`${API_URL}/provider/create-checkout-session`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan, origin_url: window.location.origin, accepted_terms: acceptTerms || prevAccepted })
      });
      let data = null;
      try {
        const text = await res.text();
        data = JSON.parse(text);
      } catch (e) {
        data = null;
      }
      if (res.ok && data?.checkout_url) {
        window.location.href = data.checkout_url;
        return; // Don't clear loading - we're navigating away
      } else {
        setError(data?.detail || 'Unable to start checkout. Please try again.');
        setCheckoutLoading('');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      setCheckoutLoading('');
    }
  };

  const handleManageBilling = async () => {
    setPortalLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/stripe/customer-portal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ return_url: `${window.location.origin}/provider/subscription` })
      });
      let data = null;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : null;
      } catch { data = null; }
      if (res.ok && data?.portal_url) {
        window.location.href = data.portal_url;
        return; // Don't clear loading - we're navigating away
      }
      setError(data?.detail || 'Billing portal not available yet.');
      setPortalLoading(false);
    } catch (err) {
      setError('Network error. Please try again.');
      setPortalLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  const current = subscription?.subscription;
  const isActive = current && ['active', 'trial'].includes(current.status);

  return (
    <div data-testid="provider-subscription-page">
      <div className="provider-page-header"><h1>Subscription</h1><p>Manage your HandyHub subscription</p></div>
      {isActive ? (
        <div className="dashboard-card" style={{ marginBottom: '24px' }}>
          <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '14px' }}>Current Plan</h3>
          <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <div><span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>Plan</span><p style={{ fontWeight: 600, textTransform: 'capitalize' }}>{current.plan}</p></div>
              <div><span style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>Status</span><p><span className="status-badge status-completed">{current.status === 'trial' ? 'Trial Active' : 'Active'}</span></p></div>
            </div>
            <button
              className="btn-outline"
              onClick={handleManageBilling}
              disabled={portalLoading}
              data-testid="manage-billing-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <CreditCard size={14} />
              {portalLoading ? 'Opening...' : 'Manage Billing'}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 'var(--radius-md)', padding: '14px 18px', marginBottom: '24px', fontSize: '0.875rem', color: '#92400E' }}>
          You don't have an active subscription. Choose a plan below.
        </div>
      )}

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}

      {!isActive && !prevAccepted && (
        <div className="dashboard-card" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '20px', padding: '14px 18px' }}>
          <input
            type="checkbox"
            id="sub-accept-terms"
            checked={acceptTerms}
            onChange={(e) => { setAcceptTerms(e.target.checked); e.target.setCustomValidity(''); }}
            onInvalid={(e) => e.target.setCustomValidity('Please read and accept the Terms of Service and Privacy Policy by ticking the box.')}
            data-testid="sub-accept-terms-checkbox"
            style={{ marginTop: '3px', cursor: 'pointer', width: 'auto' }}
          />
          <label htmlFor="sub-accept-terms" style={{ fontSize: '0.8125rem', color: 'var(--text-dark)', lineHeight: 1.5, cursor: 'pointer' }}>
            I agree to the{' '}
            <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)', fontWeight: 600 }}>Privacy Policy</Link>.
          </label>
        </div>
      )}

      <div className="subscription-cards">
        <div className={`subscription-card ${isActive && current?.plan === 'monthly' ? 'current' : ''}`} data-testid="plan-monthly" style={{ position: 'relative' }}>
          <h3>Monthly</h3>
          <div className="price">{country.currencyPrefix}49<span> + {country.gstLabel} / month</span></div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', margin: '2px 0 8px' }}>{country.gstLabel} calculated at checkout</p>
          <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0' }}>
            {['Unlimited quote leads', 'Business profile', 'Customer messaging'].map(f => (
              <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0', justifyContent: 'center', fontSize: '0.875rem', color: 'var(--text-muted)' }}><CheckCircle size={14} style={{ color: 'var(--success)' }} /> {f}</li>
            ))}
          </ul>
          {isActive && current?.plan === 'monthly' ? (
            <button className="btn-outline" style={{ width: '100%', justifyContent: 'center' }} disabled>Current Plan</button>
          ) : (
            <button className="btn-outline" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleCheckout('monthly')} disabled={!!checkoutLoading} data-testid="btn-monthly">
              {checkoutLoading === 'monthly' ? 'Redirecting...' : 'Start Monthly Plan'}
            </button>
          )}
        </div>
        <div className={`subscription-card ${isActive && current?.plan === 'yearly' ? 'current' : ''}`} style={{ borderColor: 'var(--accent)', position: 'relative' }} data-testid="plan-yearly">
          <div style={{ position: 'absolute', top: '-11px', left: '50%', transform: 'translateX(-50%)', padding: '5px 14px', background: 'var(--accent)', color: 'var(--navy-start)', borderRadius: '50px', fontSize: '0.6875rem', fontWeight: 700 }}>Best Value</div>
          <h3>Yearly</h3>
          <div className="price">{country.currencyPrefix}{country.yearlyPrice}<span> + {country.gstLabel} / year</span></div>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.6875rem', margin: '2px 0 4px' }}>{country.gstLabel} calculated at checkout</p>
          <div style={{ color: 'var(--success)', fontWeight: 600, fontSize: '0.875rem' }} data-testid="yearly-savings-label">{country.yearlySavingsLabel}</div>
          <ul style={{ listStyle: 'none', padding: 0, margin: '16px 0' }}>
            {['Everything in Monthly', 'Priority listing', 'Featured badge'].map(f => (
              <li key={f} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0', justifyContent: 'center', fontSize: '0.875rem', color: 'var(--text-muted)' }}><CheckCircle size={14} style={{ color: 'var(--success)' }} /> {f}</li>
            ))}
          </ul>
          {isActive && current?.plan === 'yearly' ? (
            <button className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} disabled>Current Plan</button>
          ) : (
            <button className="btn-primary" style={{ width: '100%', justifyContent: 'center' }} onClick={() => handleCheckout('yearly')} disabled={!!checkoutLoading} data-testid="btn-yearly">
              {checkoutLoading === 'yearly' ? 'Redirecting...' : 'Start Yearly Plan'}
            </button>
          )}
        </div>
      </div>
      <p style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8125rem', marginTop: '24px' }}>Choose a plan to activate your HandyHub provider subscription. You can manage your subscription from this page.</p>
    </div>
  );
};


// ============================================
// Checkout Success / Confirmation Page
// ============================================
export const CheckoutSuccessPage = () => {
  const { session, loading: authLoading } = useAuth();
  const { country } = useCountry();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sessionWaitCount, setSessionWaitCount] = useState(0);
  const attemptRef = React.useRef(0);

  useEffect(() => {
    if (authLoading) return;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      // No session_id — try subscription fallback immediately
      if (session?.access_token) {
        checkSubscriptionFallback();
      } else {
        setLoading(false);
        setError('Payment session not found. If payment was successful, check your provider dashboard.');
      }
      return;
    }

    if (!session?.access_token) {
      if (sessionWaitCount < 5) {
        const timer = setTimeout(() => setSessionWaitCount(c => c + 1), 1000);
        return () => clearTimeout(timer);
      }
      setLoading(false);
      setError('Your login session expired during checkout. Please log in again — your payment was processed successfully and your subscription is active.');
      return;
    }

    // Reset attempt counter when session becomes available
    if (attemptRef.current === 0) {
      verifyWithRetry(sessionId, 0);
    }
  }, [session, authLoading, sessionWaitCount]);

  const verifyWithRetry = async (sessionId, attempt) => {
    attemptRef.current = attempt + 1;
    setLoading(true);
    console.log(`[CheckoutSuccess] Attempt ${attempt + 1}/5 for session: ${sessionId.substring(0, 20)}...`);
    try {
      const res = await fetch(`${API_URL}/provider/checkout-status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      let data = null;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = null;
      }
      if (res.ok && data) {
        console.log('[CheckoutSuccess] Verification SUCCESS:', data.payment_status, data.status);
        setStatus(data);
        setLoading(false);
        return;
      }
    } catch (err) {
      console.warn(`[CheckoutSuccess] Attempt ${attempt + 1} network error:`, err.message);
    }

    // Retry up to 5 times with 1s delay
    if (attempt < 4) {
      setTimeout(() => verifyWithRetry(sessionId, attempt + 1), 1000);
    } else {
      // All retries exhausted — check subscription table as fallback
      console.log('[CheckoutSuccess] All retries exhausted, checking subscription fallback...');
      await checkSubscriptionFallback();
    }
  };

  const checkSubscriptionFallback = async () => {
    try {
      const res = await fetch(`${API_URL}/provider/subscription`, {
        headers: { 'Authorization': `Bearer ${session.access_token}` }
      });
      let data = null;
      try {
        const text = await res.text();
        data = text ? JSON.parse(text) : null;
      } catch (e) {
        data = null;
      }
      if (res.ok && data?.subscription) {
        const sub = data.subscription;
        // Treat as success if status is trial/active OR a real Stripe subscription_id exists.
        // The webhook may not have arrived yet, but the presence of stripe_subscription_id
        // means Stripe definitively created the subscription — UI can show success.
        const liveStatus = ['trial', 'trialing', 'active'].includes(sub.status);
        const hasStripeRef = !!sub.stripe_subscription_id;
        if (liveStatus || hasStripeRef) {
          console.log('[CheckoutSuccess] Subscription fallback SUCCESS:', sub.status, sub.plan, 'hasStripeRef=', hasStripeRef);
          setStatus({
            status: 'complete',
            payment_status: 'paid',
            amount_total: sub.amount ? sub.amount * 100 : 0,
            currency: (sub.currency || country.currency).toLowerCase(),
            plan: sub.plan,
            trial_days: 180,
            verified: true,
          });
          setLoading(false);
          return;
        }
      }
    } catch (e) {
      console.warn('[CheckoutSuccess] Subscription fallback error:', e.message);
    }

    // Graceful soft-fail: after exhausted retries with no confirmed trial row, assume the
    // webhook is still being processed (Stripe guarantees eventual delivery). Show a neutral
    // "activating" screen that redirects to the dashboard — NO red error banner.
    console.log('[CheckoutSuccess] Soft-fail: showing activating screen + redirect to dashboard');
    setStatus({
      status: 'activating',
      payment_status: 'paid',
      amount_total: 0,
      currency: country.currency.toLowerCase(),
      plan: 'monthly',
      trial_days: 180,
      verified: false,
      activating: true,
    });
    setLoading(false);
    // Auto-redirect to dashboard after 6 seconds so the provider lands somewhere useful
    setTimeout(() => {
      try { navigate('/provider/dashboard'); } catch (e) { /* no-op */ }
    }, 6000);
  };

  const handleRetry = () => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    attemptRef.current = 0;
    setError('');
    setLoading(true);
    if (sessionId && session?.access_token) {
      verifyWithRetry(sessionId, 0);
    } else if (session?.access_token) {
      checkSubscriptionFallback();
    } else {
      setLoading(false);
      setError('Your login session expired. Please log in again.');
    }
  };

  if (loading) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', textAlign: 'center', padding: '24px' }}>
        <div style={{ width: '48px', height: '48px', margin: '0 auto 16px', border: '3px solid var(--border-color)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <p style={{ color: 'var(--text-muted)' }}>Verifying your payment...</p>
      </div>
    );
  }

  // Soft-fail "activating" state — webhook hasn't caught up yet, but payment WAS successful
  if (status?.activating) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', textAlign: 'center', padding: '24px' }} data-testid="checkout-activating">
        <div style={{ width: '56px', height: '56px', margin: '0 auto 18px', background: '#ECFDF5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircle size={28} style={{ color: '#059669' }} />
        </div>
        <h2 style={{ marginBottom: '12px' }}>Payment received</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '10px', lineHeight: 1.6 }}>
          Your 6-month free trial is being activated. This usually takes just a few seconds.
        </p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '24px' }}>
          Taking you to your dashboard…
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn-primary" onClick={() => navigate('/provider/dashboard')} data-testid="go-dashboard-now-btn">Go to Dashboard Now</button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '80px auto', textAlign: 'center', padding: '24px' }}>
        <div style={{ width: '56px', height: '56px', margin: '0 auto 18px', background: '#FEF2F2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <AlertCircle size={28} style={{ color: '#DC2626' }} />
        </div>
        <h2 style={{ marginBottom: '12px' }}>Payment Verification Issue</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>{error}</p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {session?.access_token && (
            <button className="btn-primary" onClick={handleRetry}>Try Again</button>
          )}
          {!session?.access_token && (
            <button className="btn-primary" onClick={() => navigate('/login')}>Log In</button>
          )}
          <button className="btn-outline" onClick={() => navigate('/provider/dashboard')}>Go to Dashboard</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '60px auto', padding: '24px' }} data-testid="checkout-success">
      <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-card)', padding: '40px 32px', textAlign: 'center' }}>
        <div style={{ width: '64px', height: '64px', margin: '0 auto 20px', background: '#ECFDF5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CheckCircle size={32} style={{ color: '#059669' }} />
        </div>

        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '12px', color: 'var(--text-dark)' }}>
          Payment method saved successfully
        </h1>

        <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6, maxWidth: '440px', margin: '0 auto 16px' }}>
          Your 6-month free trial has started. You won't be charged until the trial ends.
        </p>

        <p style={{ color: 'var(--text-muted)', fontSize: '0.9375rem', lineHeight: 1.6, maxWidth: '440px', margin: '0 auto 24px' }}>
          Your provider account is now <strong>pending admin approval</strong>. You'll receive an email confirmation once activated.
        </p>

        {status && (
          <div style={{ background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', padding: '16px', textAlign: 'left', marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Plan</span>
              <span style={{ fontWeight: 600, textTransform: 'capitalize' }}>{status.plan}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Status</span>
              <span className="status-badge status-completed">Trial Active</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Free trial</span>
              <span style={{ fontWeight: 600, color: 'var(--success)' }}>{status.trial_days} days</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', fontSize: '0.875rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>Charged today</span>
              <span style={{ fontWeight: 700 }}>{country.currencySymbol}0.00</span>
            </div>
          </div>
        )}

        <div style={{ background: 'var(--surface-hover)', borderRadius: 'var(--radius-md)', padding: '16px', textAlign: 'left', marginBottom: '28px' }}>
          <h4 style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', marginBottom: '10px' }}>What happens next</h4>
          {[
            { num: '1', text: 'Our admin team reviews your profile and documents' },
            { num: '2', text: 'You get notified once your account is approved' },
            { num: '3', text: 'Start receiving and responding to customer quote leads' },
          ].map(item => (
            <div key={item.num} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.875rem', padding: '4px 0' }}>
              <span style={{ width: '24px', height: '24px', borderRadius: '50%', background: 'var(--accent)', color: 'var(--navy-start)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 700, flexShrink: 0 }}>{item.num}</span>
              <span>{item.text}</span>
            </div>
          ))}
        </div>

        <button
          className="btn-primary"
          onClick={() => navigate('/provider/dashboard')}
          style={{ fontSize: '1rem', padding: '14px 28px' }}
          data-testid="go-to-dashboard-btn"
        >
          Go to Provider Dashboard
        </button>
      </div>
    </div>
  );
};
